import { NgClass, NgFor, NgIf } from "@angular/common";
import { Component, computed, inject, signal, type OnDestroy } from "@angular/core";
import { IonContent } from "@ionic/angular/standalone";
import { environment } from "../environments/environment";
import { breakIntoSteps } from "../../../../packages/action-intelligence/src";
import { ApiClientService, type ActionTimingChoice, type AstraGoal, type BrainCell, type CalendarConnection, type CalendarOwner, type ClassificationOrigin, type DayPlan, type DayPlanItem, type DevResetMode, type PriorityDebugItem, type ActionProposal, type ProposedTimeOption, type RecurrenceRule, type TodaySet, type UsageEventType, type UserAction, type UserVisibleActionSource } from "./api-client.service";
import { buildQuickAddPayload } from "./quick-add-payload";

type CoreScreen = "ahastra" | "richting" | "jij" | "debug";
type HiddenViewMode = "day-set" | "overview";
type Tone = "personal" | "admin" | "house" | "health" | "work";
type InputFlow = "idle" | "add" | "when" | "priority" | "repeat" | "speech" | "proposal";
type SafeReturnDays = 3 | 7 | 14;
type TimingChoice = ActionTimingChoice;
type PriorityChoice = "low" | "medium" | "high";
type RecurrenceChoice = "none" | "daily" | "weekly" | "monthly";

type SpeechRecognitionConstructor = new () => SpeechRecognitionLike;
interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  onresult: ((event: SpeechRecognitionResultEventLike) => void) | null;
  onerror: (() => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
}
interface SpeechRecognitionResultEventLike {
  readonly results: ArrayLike<ArrayLike<{ readonly transcript: string }>>;
}

const RECOVERY_TIP_POOL = [
  "Haal drie keer diep adem.",
  "Voel waar de spanning in je lijf zit.",
  "Loop even uit de situatie.",
  "Drink een glas water.",
  "Kijk één minuut naar iets stil.",
] as const;

interface PriorityItem {
  readonly id: string;
  readonly title: string;
  readonly room: string;
  readonly tone: Tone;
  readonly brainCellId?: string;
  readonly astraId?: string;
  readonly detail: string;
  readonly whyNow: string;
  readonly timingChip: string;
  readonly placementState?: "essentials" | "today" | "later" | "hidden" | "scheduled" | "overdue";
  readonly placementReason: string;
  readonly steps: readonly string[];
  readonly calendar?: boolean;
  readonly directionNote?: string;
  readonly source: UserVisibleActionSource;
  readonly classificationOrigin?: ClassificationOrigin;
  readonly calendarOwner?: CalendarOwner;
  readonly recurrence?: RecurrenceRule;
  readonly urgent: boolean;
  readonly urgentConsequence: string;
}

interface HiddenActionGroup {
  readonly id: "today" | "tomorrow" | "this_week" | "next_week" | "later";
  readonly title: string;
  readonly count: number;
  readonly items: readonly PriorityItem[];
}

interface AstraDirection {
  readonly id: string;
  readonly title: string;
  readonly label: string;
  readonly description: string;
  readonly why: string;
  readonly recent: readonly string[];
  readonly rooms: readonly string[];
  readonly actions: readonly string[];
}

interface BrainCellRoom {
  readonly id: string;
  readonly label: string;
  readonly sense: string;
  readonly tone: Tone;
  readonly held: readonly HeldItem[];
}

interface HeldItem {
  readonly type: string;
  readonly title: string;
}

interface ProfileSection {
  readonly id: string;
  readonly title: string;
  readonly status: string;
  readonly lines: readonly string[];
}

interface CompletionFeedback {
  readonly title: string;
  readonly message: string;
}

interface Proposal {
  readonly title: string;
  readonly receipt: string;
  readonly room: string;
  readonly tone: Tone;
  readonly astra: string;
  readonly timing: string;
  readonly attention: string;
  readonly priority?: string;
  readonly recurrence?: string;
  readonly userTime?: TimingChoice;
  readonly userPriority?: PriorityChoice;
  readonly recurrenceChoice?: RecurrenceChoice;
  readonly recurrenceRule?: RecurrenceRule;
  readonly timeOptions: readonly ProposedTimeOption[];
  readonly selectedTimeOptionId?: string;
  readonly brainCellId?: string;
  readonly astraId?: string;
  readonly dueDate?: string;
  readonly lockedDay?: string;
  readonly lockedAt?: string;
  readonly manualDate?: string;
  readonly manualTime?: string;
  readonly nonNegotiable: boolean;
  readonly size: "tiny" | "small" | "normal" | "medium" | "large";
}

@Component({
  selector: "ahastra-shell-page",
  standalone: true,
  imports: [NgClass, NgFor, NgIf, IonContent],
  styles: [
    `
      :host {
        --paper: #f5f1e8;
        --paper-raised: #fcfaf4;
        --paper-sunk: #ede8dc;
        --edge: #e2dbcb;
        --edge-strong: #d2c9b5;
        --ink: #2c2a24;
        --ink-soft: #6a6358;
        --ink-faint: #9d9586;
        --astra: #ce8a2e;
        --astra-ink: #6e4a12;
        --astra-tint: #f0e2c6;
        --astra-edge: #e4d2ae;
        --house: #b26b4a;
        --house-fill: #efe0d6;
        --house-ink: #6e3c25;
        --personal: #6f9079;
        --personal-fill: #e0e8e1;
        --personal-ink: #395046;
        --admin: #7c7596;
        --admin-fill: #e4e1ea;
        --admin-ink: #443e60;
        --health: #c2922f;
        --health-fill: #f0e7cf;
        --health-ink: #79570f;
        --work: #4f8196;
        --work-fill: #dae9ee;
        --work-ink: #2f5360;
        --noise-fill: #eeebe2;
        --noise-edge: #dfd9cb;
        --radius-card: 16px;
        --radius-pill: 999px;
        display: block;
      }

      ion-content::part(background) {
        background: #ece7db;
      }

      button,
      input {
        font: inherit;
      }

      button {
        cursor: pointer;
      }

      .stage {
        height: 100dvh;
        min-height: 100%;
        display: grid;
        justify-items: center;
        padding: 22px 14px 34px;
        overflow: hidden;
        color: var(--ink);
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, Roboto, Helvetica, Arial, sans-serif;
      }

      .phone {
        width: min(390px, 100%);
        height: min(810px, calc(100dvh - 56px));
        min-height: min(810px, calc(100dvh - 56px));
        position: relative;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        border: 0.5px solid var(--edge-strong);
        border-radius: 42px;
        background: var(--paper);
        box-shadow: 0 1px 0 rgba(255, 255, 255, 0.6) inset, 0 10px 40px -16px rgba(44, 42, 36, 0.4);
      }

      .statusbar {
        height: 52px;
        flex: 0 0 auto;
        display: flex;
        align-items: flex-end;
        justify-content: space-between;
        gap: 12px;
        padding: 0 26px 6px;
        font-size: 14px;
        font-weight: 600;
      }

      .active-date-chip {
        color: var(--ink-soft);
        font-size: 12px;
        white-space: nowrap;
      }

      @media (max-width: 430px) {
        .stage {
          width: 100%;
          min-height: 100dvh;
          padding: 0;
          box-sizing: border-box;
        }

        .phone {
          width: 100%;
          height: 100dvh;
          min-height: 100dvh;
          border: 0;
          border-radius: 0;
          box-shadow: none;
        }

        .statusbar {
          display: none;
        }
      }

      .screen {
        flex: 1;
        min-height: 0;
        display: flex;
        flex-direction: column;
        overflow: hidden;
      }

      .scroll {
        flex: 1;
        overflow-y: auto;
        padding: 0 22px 168px;
        scrollbar-width: none;
      }

      .scroll::-webkit-scrollbar {
        display: none;
      }

      .home-lead,
      .astra-lead,
      .cells-lead {
        padding: 18px 2px 12px;
        border: 0;
        background: transparent;
        text-align: left;
        font-size: 13px;
        font-weight: 650;
        letter-spacing: 0.07em;
        text-transform: uppercase;
        color: var(--ink-faint);
      }

      .home-lead.now,
      .astra-lead {
        color: var(--astra-ink);
      }

      .quiet-line.empty {
        height: 4px;
        margin: -3px 2px 10px;
      }

      .priority-stack,
      .detail-stack,
      .setting-list {
        display: grid;
        gap: 12px;
      }

      .support-label {
        margin: 2px 2px -4px;
        color: var(--ink-faint);
        font-size: 13px;
      }

      .priority-card,
      .detail-card,
      .setting-card,
      .relief-card {
        position: relative;
        display: flex;
        align-items: center;
        gap: 14px;
        min-height: 82px;
        padding: 17px;
        border: 0.5px solid var(--edge);
        border-radius: var(--radius-card);
        background: var(--paper-raised);
        color: var(--ink);
        text-align: left;
      }

      .priority-card {
        width: 100%;
      }

      .priority-card.answer {
        min-height: 96px;
        align-items: flex-start;
        padding: 16px 16px 14px;
        border-color: rgba(206, 138, 46, 0.32);
        background: linear-gradient(135deg, #fffaf0, var(--paper-raised) 70%);
        box-shadow: 0 12px 34px -26px rgba(44, 42, 36, 0.48);
      }

      .priority-card.rest {
        min-height: 82px;
        opacity: 0.9;
      }

      .priority-card.started {
        border-color: rgba(206, 138, 46, 0.58);
        animation: startedPulse 1800ms ease-in-out infinite;
      }

      @keyframes startedPulse {
        0%, 100% { box-shadow: 0 0 0 0 rgba(206, 138, 46, 0.15); }
        50% { box-shadow: 0 0 0 5px rgba(206, 138, 46, 0.08); }
      }

      .priority-card.hero {
        min-height: 164px;
        align-items: flex-start;
        padding: 22px 20px 20px;
      }

      .priority-card .mark,
      .hero .mark,
      .detail-mark {
        width: 4px;
        align-self: stretch;
        min-height: 34px;
        flex: 0 0 auto;
        border-radius: var(--radius-pill);
        background: var(--tone);
      }

      .hero .mark,
      .detail-mark {
        min-height: 72px;
      }

      .priority-body,
      .detail-body,
      .setting-body {
        flex: 1;
        min-width: 0;
      }

      .priority-card.answer .priority-body {
        display: grid;
        align-content: start;
        gap: 8px;
      }

      .priority-card.answer .start-pill,
      .priority-card.answer .secondary-pill {
        margin-top: 0;
      }

      .priority-headline {
        display: grid;
        grid-template-columns: minmax(0, 1fr) auto;
        gap: 10px;
        align-items: start;
      }

      .card-time {
        color: var(--ink-soft);
        font-size: 12px;
        line-height: 1.2;
        white-space: nowrap;
      }

      .priority-card-actions,
      .started-actions {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 8px;
      }

      .source-icon {
        display: inline-grid;
        place-items: center;
        width: 24px;
        height: 24px;
        border-radius: 50%;
        background: var(--paper-sunk);
        color: var(--ink-soft);
        font-size: 13px;
      }

      .priority-card.answer .start-pill,
      .priority-card.answer .secondary-pill {
        width: max-content;
        max-width: 100%;
      }

      @media (max-width: 360px) {
        .priority-card.answer .start-pill {
          width: 100%;
        }
      }

      .priority-title,
      .detail-title,
      .setting-title {
        margin: 0;
        font-size: 16.5px;
        font-weight: 520;
        line-height: 1.3;
        letter-spacing: -0.01em;
      }

      .priority-title {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .hero .priority-title,
      .detail-title {
        font-size: 22px;
        font-weight: 620;
        line-height: 1.25;
      }

      .priority-meta,
      .detail-meta,
      .setting-status {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 7px;
        margin-top: 4px;
        color: var(--ink-soft);
        font-size: 13px;
      }

      .why-button {
        margin: 12px 0 0;
        padding: 0;
        border: 0;
        background: transparent;
        color: var(--ink-faint);
        font-size: 13px;
      }

      .why-copy {
        margin: 8px 0 0;
        color: var(--ink-soft);
        font-size: 14px;
        line-height: 1.35;
      }

      .start-pill,
      .secondary-pill {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        margin-top: 16px;
        padding: 10px 20px;
        border: 0;
        border-radius: var(--radius-pill);
        background: var(--ink);
        color: var(--paper);
        font-size: 15px;
        font-weight: 520;
      }

      .secondary-pill,
      .start-pill.subtle {
        background: var(--paper-sunk);
        color: var(--ink-soft);
        border: 0.5px solid var(--edge);
      }

      .context-card {
        width: 100%;
        display: flex;
        align-items: flex-start;
        gap: 13px;
        margin-top: 13px;
        padding: 15px 17px;
        border: 0.5px solid var(--noise-edge);
        border-radius: var(--radius-card);
        background: var(--noise-fill);
        color: inherit;
        font: inherit;
        text-align: left;
      }

      .context-dot {
        width: 7px;
        height: 7px;
        flex: 0 0 auto;
        margin-top: 7px;
        border-radius: 50%;
        background: #b8b0a0;
      }

      .context-text,
      .detail-copy,
      .held-copy {
        display: block;
        color: #5a5346;
        font-size: 15.5px;
        line-height: 1.4;
      }

      .detail-copy {
        margin: 10px 0 0;
      }

      .home-layout {
        flex: 1;
        min-height: 0;
        display: flex;
        flex-direction: column;
        padding: 0 18px;
      }

      .home-fixed {
        flex: 0 0 auto;
      }

      .home-secondary-scroll {
        flex: 1;
        min-height: 0;
        overflow-y: auto;
        padding-bottom: 168px;
        scrollbar-width: none;
      }

      .screen-toolbar {
        position: sticky;
        top: 0;
        z-index: 12;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        min-height: 44px;
        padding: calc(8px + env(safe-area-inset-top, 0px)) 0 8px;
        background: linear-gradient(180deg, var(--paper) 72%, rgba(245, 241, 232, 0));
      }

      .screen-toolbar .back-button {
        margin: 0;
      }

      .home-secondary-scroll::-webkit-scrollbar {
        display: none;
      }

      .home-actions-row {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 10px;
        margin: 12px 0 10px;
      }

      .mode-button {
        min-height: 46px;
        border: 0.5px solid var(--edge);
        border-radius: var(--radius-pill);
        background: var(--paper-sunk);
        color: var(--ink-soft);
        font: inherit;
        font-size: 15px;
        font-weight: 560;
      }

      .mode-button.primary {
        border-color: rgba(44, 42, 36, 0.18);
        background: var(--ink);
        color: var(--paper);
      }

      .started-badge {
        display: inline-flex;
        width: max-content;
        margin-top: 12px;
        padding: 4px 9px;
        border-radius: var(--radius-pill);
        background: rgba(206, 138, 46, 0.14);
        color: var(--astra-ink);
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.08em;
      }

      .started-actions {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        margin-top: 0;
      }

      .started-actions .start-pill,
      .started-actions .secondary-pill {
        margin-top: 0;
        padding-inline: 14px;
      }

      .info-button {
        width: 28px;
        height: 28px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border: 0.5px solid var(--edge);
        border-radius: 50%;
        background: transparent;
        color: var(--ink-faint);
        font-size: 13px;
      }

      .horizon {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 7px;
        padding: 12px 0 2px;
        border: 0;
        background: transparent;
        color: var(--ink-faint);
        font-size: 13px;
      }

      .overload-copy {
        padding: 30px 0 8px;
      }

      .overload-copy h1,
      .panel-title {
        margin: 0;
        font-size: 25px;
        font-weight: 640;
        letter-spacing: -0.02em;
      }

      .small-step,
      .back-button {
        margin: 14px 0 0 18px;
        border: 0;
        background: transparent;
        color: var(--ink-soft);
        font-size: 13.5px;
      }

      .small-step span,
      .back-button span {
        border-bottom: 0.5px dashed var(--ink-faint);
        padding-bottom: 1px;
      }

      .astra-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 11px;
      }

      .astra-card {
        min-height: 112px;
        display: flex;
        flex-direction: column;
        gap: 8px;
        padding: 15px;
        border: 0.5px solid var(--astra-edge);
        border-radius: var(--radius-card);
        background: var(--astra-tint);
        text-align: left;
      }

      .astra-card.add {
        align-items: center;
        justify-content: center;
        text-align: center;
        background: rgba(240, 226, 198, 0.48);
      }

      .star {
        color: var(--astra);
        font-size: 19px;
        line-height: 1;
      }

      .astra-anchor {
        color: var(--astra-ink);
        font-size: 17px;
        font-weight: 620;
        line-height: 1.25;
      }

      .astra-label,
      .mini-label {
        color: rgba(110, 74, 18, 0.72);
        font-size: 12px;
      }

      .meaning-divider {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 24px 2px 20px;
      }

      .meaning-divider .line {
        height: 0.5px;
        flex: 1;
        background: var(--edge-strong);
      }

      .meaning-divider .label {
        color: var(--ink-faint);
        font-size: 11.5px;
        font-weight: 650;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        white-space: nowrap;
      }

      .room-list {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 11px;
      }

      .room-card {
        min-height: 104px;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        padding: 15px 16px;
        border: 0;
        border-radius: 24px 18px 26px 18px;
        background:
          radial-gradient(ellipse at 100% 0%, rgba(255, 255, 255, 0.36), transparent 58%),
          var(--fill);
        color: var(--room-ink);
        text-align: left;
      }

      .room-card:nth-child(2n) {
        border-radius: 18px 24px 18px 26px;
      }

      .room-top {
        display: flex;
        align-items: center;
        gap: 10px;
      }

      .room-dot {
        width: 11px;
        height: 11px;
        flex: 0 0 auto;
        border-radius: 50%;
        background: transparent;
        box-shadow: inset 0 0 0 2px var(--tone);
      }

      .room-label {
        font-size: 16px;
        font-weight: 650;
      }

      .room-sense {
        margin-top: 16px;
        font-size: 13px;
        line-height: 1.35;
        opacity: 0.78;
      }

      .detail-actions,
      .two-actions {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        margin-top: 14px;
      }

      .detail-actions .start-pill,
      .detail-actions .secondary-pill,
      .two-actions .start-pill,
      .two-actions .secondary-pill {
        margin-top: 0;
        padding-inline: 15px;
      }

      .mini-list {
        display: grid;
        gap: 8px;
        margin-top: 14px;
      }

      .mini-row,
      .held-row {
        display: flex;
        gap: 10px;
        align-items: flex-start;
        padding: 12px 13px;
        border: 0.5px solid var(--edge);
        border-radius: 14px;
        background: var(--paper-sunk);
        color: var(--ink-soft);
        text-align: left;
      }

      .mini-row.done {
        opacity: 0.55;
      }

      .mini-row.done .held-copy {
        text-decoration: line-through;
      }

      .held-type {
        min-width: 72px;
        color: var(--ink-faint);
        font-size: 11px;
        font-weight: 650;
        letter-spacing: 0.06em;
        text-transform: uppercase;
      }

      .relief-card {
        min-height: auto;
        margin-bottom: 12px;
        border-color: rgba(111, 144, 121, 0.34);
        background: #edf2eb;
        color: var(--personal-ink);
        opacity: 1;
        transform: translateY(0);
        transition: opacity 320ms ease, transform 320ms ease;
      }

      .relief-card.is-fading {
        opacity: 0;
        transform: translateY(4px);
      }

      .today-enough {
        margin-top: 12px;
      }

      .empty-state {
        min-height: 260px;
        display: grid;
        place-items: center;
        padding: 30px 8px;
        color: var(--ink-soft);
        font-size: 19px;
        line-height: 1.4;
        text-align: center;
      }

      .input-bar-wrap {
        flex: 0 0 auto;
        position: absolute;
        left: 0;
        right: 0;
        bottom: calc(76px + env(safe-area-inset-bottom, 0px) + env(keyboard-inset-height, 0px));
        z-index: 20;
        padding: 12px 18px 8px;
        background: linear-gradient(180deg, rgba(245, 241, 232, 0), var(--paper) 34%);
      }

      .input-bar {
        display: flex;
        align-items: center;
        gap: 9px;
        margin: 0;
        padding: 7px 9px;
        border: 0.5px solid var(--edge);
        border-radius: var(--radius-pill);
        background: var(--paper-sunk);
      }

      .input-field {
        width: 100%;
        min-width: 0;
        flex: 1;
        border: 0;
        outline: 0;
        background: transparent;
        color: var(--ink);
        font-size: 15px;
        cursor: text;
        pointer-events: auto;
        -webkit-user-select: text;
        user-select: text;
        touch-action: manipulation;
      }

      .input-field::placeholder {
        color: var(--ink-faint);
      }

      .round-icon {
        width: 38px;
        height: 38px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex: 0 0 auto;
        border: 0.5px solid var(--edge);
        border-radius: 50%;
        background: var(--paper-raised);
        color: var(--ink-soft);
        font-size: 18px;
      }

      .bottom-nav {
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 19;
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 6px;
        padding: 8px 18px calc(12px + env(safe-area-inset-bottom, 0px));
        background: linear-gradient(180deg, rgba(245, 241, 232, 0), var(--paper) 22%);
      }

      .bottom-nav button {
        display: grid;
        justify-items: center;
        gap: 3px;
        padding: 8px 4px 7px;
        border: 0;
        border-radius: 18px;
        background: transparent;
        color: var(--ink-faint);
        font-size: 11.5px;
      }

      .bottom-nav button.active {
        background: var(--paper-raised);
        color: var(--ink);
      }

      .nav-icon {
        font-size: 16px;
        line-height: 1;
      }

      .flow-sheet {
        position: absolute;
        left: 18px;
        right: 18px;
        bottom: calc(146px + env(safe-area-inset-bottom, 0px) + env(keyboard-inset-height, 0px));
        z-index: 24;
        display: grid;
        gap: 12px;
        max-height: calc(100dvh - 230px);
        overflow-y: auto;
        padding: 18px;
        border: 0.5px solid var(--edge-strong);
        border-radius: 24px;
        background: rgba(252, 250, 244, 0.98);
        box-shadow: 0 18px 44px -28px rgba(44, 42, 36, 0.52);
      }

      .flow-sheet.top-sheet {
        top: 72px;
        bottom: auto;
        max-height: calc(100dvh - 150px);
      }

      .flow-title {
        margin: 0;
        font-size: 19px;
        font-weight: 650;
        letter-spacing: -0.01em;
      }

      .sheet-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
      }

      .sheet-nav {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
      }

      .sheet-back,
      .sheet-close {
        border: 0;
        background: transparent;
        color: var(--ink-soft);
      }

      .sheet-back {
        padding: 0;
        font-size: 13.5px;
      }

      .sheet-back span {
        border-bottom: 0.5px dashed var(--ink-faint);
        padding-bottom: 1px;
      }

      .sheet-close {
        width: 32px;
        height: 32px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border: 0.5px solid var(--edge);
        border-radius: 50%;
        background: var(--paper-sunk);
        font-size: 18px;
        line-height: 1;
      }

      .flow-input {
        width: 100%;
        min-height: 44px;
        padding: 0 14px;
        border: 0.5px solid var(--edge);
        border-radius: var(--radius-pill);
        outline: 0;
        background: var(--paper-sunk);
        color: var(--ink);
      }

      .proposal-grid {
        display: grid;
        gap: 8px;
      }

      .proposal-row {
        display: flex;
        justify-content: space-between;
        gap: 16px;
        padding: 9px 0;
        border-bottom: 0.5px solid var(--edge);
        color: var(--ink-soft);
        font-size: 14px;
      }

      .proposal-row strong {
        color: var(--ink);
        font-weight: 560;
        text-align: right;
      }

      .proposal-edit-group {
        display: grid;
        gap: 8px;
      }

      .proposal-edit-label {
        color: var(--ink-faint);
        font-size: 12px;
        font-weight: 650;
        letter-spacing: 0.06em;
        text-transform: uppercase;
      }

      .proposal-edit-actions {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
      }

      .proposal-edit-actions .secondary-pill,
      .proposal-edit-actions .start-pill {
        margin-top: 0;
        padding-inline: 14px;
      }

      .item-labels {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
        margin-top: 8px;
      }

      .item-label {
        display: inline-flex;
        width: max-content;
        padding: 3px 8px;
        border: 0.5px solid var(--edge);
        border-radius: var(--radius-pill);
        background: rgba(252, 250, 244, 0.64);
        color: var(--ink-soft);
        font-size: 11px;
        font-weight: 650;
        letter-spacing: 0.04em;
        text-transform: uppercase;
      }

      .item-label.dev {
        border-style: dashed;
      }

      .proposal-time-option {
        width: 100%;
        display: grid;
        gap: 3px;
        padding: 11px 12px;
        border: 0.5px solid var(--edge);
        border-radius: 16px;
        background: var(--paper-sunk);
        color: var(--ink);
        text-align: left;
      }

      .proposal-time-option.active {
        border-color: rgba(206, 138, 46, 0.55);
        background: var(--astra-tint);
      }

      .proposal-time-option small {
        color: var(--ink-soft);
      }

      .manual-time-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 8px;
      }

      .manual-date-grid {
        display: grid;
        grid-template-columns: 0.7fr 0.7fr 1fr;
        gap: 8px;
      }

      .proposal-save-row {
        position: sticky;
        bottom: -8px;
        z-index: 2;
        padding-top: 8px;
        background: linear-gradient(180deg, rgba(252, 250, 244, 0), rgba(252, 250, 244, 0.98) 30%);
      }

      .dev-reset-entry {
        margin-top: 10px;
        width: 100%;
        border: 0.5px dashed var(--edge-strong);
        border-radius: 16px;
        padding: 11px 12px;
        background: rgba(252, 250, 244, 0.66);
        color: var(--ink-soft);
        text-align: left;
        font-size: 13px;
      }

      .replan-note,
      .urgent-consequence,
      .reset-copy,
      .day-done-final {
        margin: 0;
        color: var(--ink-soft);
        font-size: 14px;
        line-height: 1.45;
      }

      .urgent-consequence {
        padding: 12px;
        border: 0.5px solid var(--astra-edge);
        border-radius: 16px;
        background: var(--astra-tint);
        color: var(--astra-ink);
      }

      .day-done-sheet {
        text-align: center;
      }

      .day-done-animation {
        position: relative;
        width: 94px;
        height: 94px;
        justify-self: center;
        display: grid;
        place-items: center;
      }

      .day-done-ring {
        position: absolute;
        inset: 14px;
        border: 0.5px solid var(--astra-edge);
        border-radius: 50%;
        animation: farewellRing 1300ms ease-out both;
      }

      .day-done-ring.two {
        inset: 4px;
        animation-delay: 120ms;
      }

      .day-done-star {
        position: absolute;
        color: var(--astra);
        font-size: 20px;
        animation: farewellFloat 1600ms ease-out both;
      }

      .day-done-star.main {
        position: static;
        width: 62px;
        height: 62px;
        display: grid;
        place-items: center;
        border-radius: 50%;
        background: var(--astra-tint);
        font-size: 32px;
        animation: farewellGlow 900ms ease-out both;
      }

      .day-done-star.one { top: 8px; right: 16px; animation-delay: 90ms; }
      .day-done-star.two { bottom: 12px; left: 10px; animation-delay: 180ms; }
      .day-done-star.three { bottom: 10px; right: 9px; animation-delay: 260ms; }

      .day-done-summary {
        display: grid;
        gap: 10px;
        animation: summaryFade 5s ease forwards;
      }

      @keyframes farewellGlow {
        from { transform: scale(0.82); opacity: 0.4; }
        to { transform: scale(1); opacity: 1; }
      }

      @keyframes farewellRing {
        from { transform: scale(0.52); opacity: 0.7; }
        to { transform: scale(1.12); opacity: 0; }
      }

      @keyframes farewellFloat {
        from { transform: translateY(8px) scale(0.82); opacity: 0; }
        45% { opacity: 1; }
        to { transform: translateY(-8px) scale(1); opacity: 0.82; }
      }

      @keyframes summaryFade {
        0%, 76% { opacity: 1; transform: translateY(0); }
        100% { opacity: 0; transform: translateY(-4px); }
      }

      .tone-personal { --tone: var(--personal); --fill: var(--personal-fill); --room-ink: var(--personal-ink); }
      .tone-admin { --tone: var(--admin); --fill: var(--admin-fill); --room-ink: var(--admin-ink); }
      .tone-house { --tone: var(--house); --fill: var(--house-fill); --room-ink: var(--house-ink); }
      .tone-health { --tone: var(--health); --fill: var(--health-fill); --room-ink: var(--health-ink); }
      .tone-work { --tone: var(--work); --fill: var(--work-fill); --room-ink: var(--work-ink); }

      .completion-pop {
        text-align: center;
        z-index: 30;
      }

      .completion-orb {
        position: relative;
        width: 76px;
        height: 76px;
        justify-self: center;
        display: grid;
        place-items: center;
        border-radius: 50%;
        background: var(--astra-tint);
        color: var(--astra-ink);
        font-size: 34px;
        animation: completionGlow 900ms ease-out both;
      }

      .completion-orb::before,
      .completion-orb::after {
        content: "";
        position: absolute;
        inset: -7px;
        border: 0.5px solid var(--astra-edge);
        border-radius: 50%;
        animation: completionRing 1300ms ease-out both;
      }

      .completion-orb::after {
        inset: -15px;
        animation-delay: 120ms;
      }

      .completion-title {
        margin: 0;
        font-size: 20px;
        font-weight: 650;
        letter-spacing: -0.01em;
      }

      @keyframes completionGlow {
        from { transform: scale(0.76); opacity: 0.2; }
        60% { transform: scale(1.06); opacity: 1; }
        to { transform: scale(1); opacity: 1; }
      }

      @keyframes completionRing {
        from { transform: scale(0.55); opacity: 0.62; }
        to { transform: scale(1.12); opacity: 0; }
      }

      @supports (height: 100svh) {
        .stage { height: 100svh; }
      }

      @media (max-width: 410px) {
        .stage { padding: 0; }
        .phone { height: 100svh; min-height: 100svh; border-radius: 0; border: 0; width: 100%; }
        .home-lead { padding-top: 12px; }
        .priority-card.answer { min-height: 92px; }
        .priority-card.rest { min-height: 78px; }
      }
    `,
  ],
  template: `
    <ion-content [fullscreen]="true">
      <main class="stage">
        <article class="phone">
          <div class="statusbar"><span>{{ currentTimeLabel() }}</span><span class="active-date-chip">{{ activeAhastraLabel() }}</span></div>

          <section class="screen" *ngIf="screen() === 'ahastra'" data-screenshot="ahastra">
            <div class="scroll" *ngIf="activeAction() as action; else ahastraRoot">
              <div class="screen-toolbar">
                <button class="back-button" type="button" (click)="closeAction()"><span>terug naar nu</span></button>
                <button type="button" class="sheet-close" aria-label="Sluiten" (click)="closeAction()">×</button>
              </div>
              <div class="home-lead now">Dit ene ding</div>
              <article class="detail-card" [ngClass]="toneClass(action.tone)">
                <span class="detail-mark"></span>
                <div class="detail-body">
                  <h1 class="detail-title">{{ action.title }}</h1>
                  <div class="detail-meta">
                    <span>{{ action.room }}</span><span>{{ action.timingChip }}</span><span>· {{ sourceLabel(action) }}</span>
                  </div>
                  <div class="item-labels">
                    <span class="item-label">{{ sourceLabel(action) }}</span>
                    <span class="item-label" *ngIf="action.calendarOwner">{{ ownerLabel(action.calendarOwner) }}</span>
                    <span class="item-label dev" *ngIf="developerMode() && action.classificationOrigin">{{ action.classificationOrigin }}</span>
                  </div>
                  <p class="detail-copy">{{ action.detail }}</p>
                  <p class="detail-copy" *ngIf="actionStarted(action.id)">Begonnen. Ahastra houdt dit vast.</p>
                  <p class="detail-copy" *ngIf="action.calendar">Bij later zetten blijft het agenda-gevolg zichtbaar.</p>
                  <div class="detail-actions">
                    <button type="button" class="secondary-pill" (click)="makeActionSmaller(action.id)">In kleine stapjes opdelen</button>
                    <button type="button" class="secondary-pill" (click)="requestSkip(action.id)">Later</button>
                    <button type="button" class="start-pill" (click)="completeWholeAction(action.id)">Gedaan</button>
                    <button type="button" class="secondary-pill" *ngIf="!action.recurrence" (click)="removeAction(action.id)">Verwijderen</button>
                    <button type="button" class="secondary-pill" *ngIf="action.recurrence" (click)="removeAction(action.id, 'occurrence')">Verwijder deze ene keer</button>
                    <button type="button" class="secondary-pill" *ngIf="action.recurrence" (click)="removeAction(action.id, 'series')">Verwijder de hele serie</button>
                  </div>
                  <div class="proposal-edit-group" *ngIf="astraRecords().length">
                    <span class="proposal-edit-label">Astra handmatig koppelen</span>
                    <div class="proposal-edit-actions">
                      <button type="button" [ngClass]="proposalChoiceClass(!actionHasAstra(action.id))" (click)="assignActionAstra(action.id, null)">Geen Astra</button>
                      <button type="button" *ngFor="let astra of astraRecords()" [ngClass]="proposalChoiceClass(actionLinkedToAstra(action.id, astra.id))" (click)="assignActionAstra(action.id, astra.id)">{{ directionTitle(astra.text) }}</button>
                    </div>
                  </div>
                </div>
              </article>

              <div class="mini-list" *ngIf="actionStepsOpen()">
                <button
                  type="button"
                  class="mini-row"
                  *ngFor="let step of action.steps"
                  [class.done]="stepDone(action.id, step)"
                  (click)="toggleStep(action.id, step)"
                >
                  <span class="held-type">stap</span>
                  <span class="held-copy">{{ step }}</span>
                </button>
              </div>
            </div>

            <ng-template #ahastraRoot>
              <div class="scroll" *ngIf="hiddenItemsOpen(); else ahastraHome">
                <div class="screen-toolbar">
                  <button class="back-button" type="button" (click)="hiddenItemsOpen.set(false)"><span>terug naar nu</span></button>
                  <button type="button" class="sheet-close" aria-label="Sluiten" (click)="hiddenItemsOpen.set(false)">×</button>
                </div>
                <div class="home-lead now">Bewaard</div>
                <article class="detail-card">
                  <span class="context-dot"></span>
                  <div class="detail-body">
                    <h1 class="detail-title">{{ hiddenViewMode() === 'overview' ? 'Later' : (moreHiddenCount() ? ('Rest van vandaag (' + moreHiddenCount() + ')') : 'Niets verborgen') }}</h1>
                    <p class="detail-copy">{{ hiddenViewMode() === 'overview' ? 'Alles eerlijk in Priority Grid-volgorde op dit moment.' : 'Alleen de rest van de gekozen dag-set.' }}</p>
                  </div>
                </article>
                <div class="priority-stack" *ngIf="hiddenActionItems().length; else emptyHiddenList">
                  <article
                    class="priority-card"
                    *ngFor="let item of hiddenActionItems()"
                    [ngClass]="toneClass(item.tone)"
                  >
                    <span class="mark"></span>
                    <div class="priority-body">
                      <h2 class="priority-title">{{ cardTitle(item) }}</h2>
                      <div class="priority-meta">
                        <span>{{ item.room }}</span>
                        <span>{{ item.timingChip }}</span>
                        <span>· {{ sourceLabel(item) }}</span>
                        <span *ngIf="developerMode() && item.classificationOrigin">· {{ item.classificationOrigin }}</span>
                      </div>
                      <div class="detail-actions">
                        <button type="button" class="start-pill" *ngIf="!isPromoted(item.id)" (click)="promoteAction(item.id)">Zet bovenaan</button>
                        <button type="button" class="secondary-pill" *ngIf="isPromoted(item.id)" (click)="unpromoteAction(item.id)">Niet bovenaan</button>
                        <button type="button" class="secondary-pill" (click)="replanHiddenItem(item.id, 'today')">Vandaag</button>
                        <button type="button" class="secondary-pill" (click)="replanHiddenItem(item.id, 'tomorrow')">Morgen</button>
                      </div>
                    </div>
                  </article>
                </div>
                <ng-template #emptyHiddenList>
                  <div class="empty-state">Geen items in deze laag.</div>
                </ng-template>
                <button class="horizon" type="button" *ngIf="hiddenViewMode() === 'day-set'" (click)="openOverviewItems()">⌄ Later</button>
              </div>

            </ng-template>

            <ng-template #ahastraHome>
              <div class="home-layout" *ngIf="!helpMode(); else oneThingHome">
                <div class="home-fixed">
                  <div class="home-lead now">Wat nu telt</div>
                  <p class="quiet-line empty" aria-hidden="true"></p>

                  <div class="priority-stack" *ngIf="visiblePriorities().length; else emptyPriorities">
                    <article
                      class="priority-card"
                      *ngFor="let item of visiblePriorities(); let first = first"
                      [class.answer]="first"
                      [class.rest]="!first"
                      [class.started]="actionStarted(item.id)"
                      [ngClass]="toneClass(item.tone)"
                    >
                      <span class="mark"></span>
                      <div class="priority-body">
                        <div class="priority-headline">
                          <h2 class="priority-title">{{ cardTitle(item) }}</h2>
                          <span class="card-time">{{ cardDateTime(item) }}</span>
                        </div>
                        <div class="priority-card-actions" *ngIf="!actionStarted(item.id)">
                          <span class="source-icon" [attr.aria-label]="sourceLabel(item)">{{ sourceIcon(item) }}</span>
                          <button type="button" class="start-pill" (click)="startAction(item.id)">Start</button>
                          <button type="button" class="secondary-pill" (click)="requestSkip(item.id)">Skip</button>
                        </div>
                        <div class="started-actions" *ngIf="actionStarted(item.id)">
                          <span class="source-icon" [attr.aria-label]="sourceLabel(item)">{{ sourceIcon(item) }}</span>
                          <button type="button" class="start-pill" (click)="completeCardAction(item.id)">Gedaan</button>
                          <button type="button" class="secondary-pill" (click)="makeActionSmaller(item.id)">kleine stapjes</button>
                          <button type="button" class="secondary-pill" (click)="requestSkip(item.id)">Later</button>
                        </div>
                      </div>
                    </article>
                  </div>

                  <ng-template #emptyPriorities>
                    <div class="empty-state">Voor nu is dit genoeg.</div>
                  </ng-template>

                  <div class="home-actions-row" *ngIf="!mvp1TestMode">
                    <button class="mode-button primary" type="button" (click)="reweighNow()">Ahastra</button>
                    <button class="mode-button" type="button" (click)="openHelpChoice()">Help</button>
                  </div>
                </div>

                <div class="home-secondary-scroll">
                  <article class="relief-card" *ngIf="safeReturnMessage()">
                    <span class="context-dot"></span>
                    <span class="context-text">{{ safeReturnMessage() }}</span>
                  </article>

                  <article class="relief-card" *ngIf="reliefMessage()" [class.is-fading]="reliefFading()">
                    <span class="context-dot"></span>
                    <span class="context-text">{{ reliefMessage() }}</span>
                  </article>

                  <article class="relief-card" *ngIf="startedCheckInVisible()">
                    <span class="context-dot"></span>
                    <span class="context-text">Je was met {{ startedActionIds().size }} dingen bezig. Hoe staat het ervoor?</span>
                    <div class="detail-actions">
                      <button type="button" class="start-pill" (click)="acceptStartedCheckIn()">Laat staan</button>
                      <button type="button" class="secondary-pill" (click)="reweighNow()">Ahastra kijkt opnieuw</button>
                    </div>
                  </article>

                  <button class="context-card" type="button" *ngIf="priorities().length || todayRestItems().length" (click)="openHiddenItems()">
                    <span class="context-dot"></span>
                    <span class="context-text">{{ savedCardText() }}</span>
                  </button>

                  <article class="relief-card" *ngIf="safeReturnPromptDays() as days">
                    <span class="context-dot"></span>
                    <span class="context-text">We beginnen hier.</span>
                    <div class="detail-actions">
                      <button type="button" class="secondary-pill" (click)="applySafeReturn(days)">Start</button>
                    </div>
                  </article>

                  <button class="dev-reset-entry" type="button" *ngIf="!environment.production" (click)="openReset()">Test reset · demo opnieuw of leeg beginnen</button>
                  <button class="horizon" type="button" *ngIf="!mvp1TestMode" (click)="openOverviewItems()">⌄ Later</button>
                </div>
              </div>
            </ng-template>

            <ng-template #oneThingHome>
              <div class="scroll">
                <div class="screen-toolbar">
                  <button class="back-button" type="button" (click)="exitOverload()"><span>terug naar nu</span></button>
                  <button type="button" class="sheet-close" aria-label="Sluiten" (click)="exitOverload()">×</button>
                </div>
                <div class="overload-copy">
                  <h1>Help</h1>
                  <p class="detail-copy">Eerst deze veilige stap.</p>
                </div>

                <article class="priority-card hero" *ngIf="priorities()[0] as one" [ngClass]="toneClass(one.tone)">
                  <span class="mark"></span>
                  <div class="priority-body">
                    <h2 class="priority-title">{{ firstStepTitle(one) }}</h2>
                    <div class="priority-meta">{{ one.room }}</div>
                    <button type="button" class="start-pill" (click)="startAction(one.id)">Begin</button>
                    <button type="button" class="secondary-pill" *ngIf="!mvp1TestMode" (click)="makeActionSmaller(one.id)">Kleiner</button>
                    <p class="detail-copy" *ngIf="actionStarted(one.id)">Begonnen. Alleen dit hoeft nu.</p>
                  </div>
                </article>

                <div class="mini-list" *ngIf="actionStepsOpen() && priorities()[0] as one">
                  <button
                    type="button"
                    class="mini-row"
                    *ngFor="let step of one.steps"
                    [class.done]="stepDone(one.id, step)"
                    (click)="toggleStep(one.id, step)"
                  >
                    <span class="held-type">stap</span>
                    <span class="held-copy">{{ step }}</span>
                  </button>
                </div>

                <button class="small-step" type="button" (click)="exitOverload()"><span>terug naar nu</span></button>
              </div>
            </ng-template>
          </section>

          <section class="screen" *ngIf="screen() === 'richting'" data-screenshot="richting">
            <div class="scroll" *ngIf="activeAstra() as astra; else richtingRooms">
              <div class="screen-toolbar">
                <button class="back-button" type="button" (click)="closeDirectionDetail()"><span>terug naar richting</span></button>
              </div>
              <div class="astra-lead">Astra</div>
              <article class="detail-card">
                <span class="star">★</span>
                <div class="detail-body">
                  <h1 class="detail-title">{{ astra.title }}</h1>
                  <div class="detail-meta">{{ astra.label }}</div>
                  <p class="detail-copy">{{ astra.description }}</p>
                  <p class="detail-copy">{{ astra.why }}</p>
                </div>
              </article>
              <div class="mini-list">
                <article class="held-row" *ngFor="let action of astra.recent">
                  <span class="held-type">recent</span>
                  <span class="held-copy">{{ action }}</span>
                </article>
                <article class="held-row" *ngFor="let room of astra.rooms">
                  <span class="held-type">bewaard</span>
                  <span class="held-copy">{{ room }}</span>
                </article>
                <article class="held-row" *ngFor="let related of astra.actions">
                  <span class="held-type">actie</span>
                  <span class="held-copy">{{ related }}</span>
                </article>
              </div>
            </div>

            <ng-template #richtingRooms>
              <div class="scroll" *ngIf="activeRoom() as room; else richtingHome">
                <div class="screen-toolbar">
                  <button class="back-button" type="button" (click)="closeDirectionDetail()"><span>terug naar richting</span></button>
                </div>
                <div class="cells-lead">Bewaard</div>
                <article class="detail-card" [ngClass]="toneClass(room.tone)">
                  <span class="detail-mark"></span>
                  <div class="detail-body">
                    <h1 class="detail-title">{{ room.label }}</h1>
                    <div class="detail-meta">{{ room.sense }}</div>
                    <p class="detail-copy">Ahastra bewaart dit voor je.</p>
                  </div>
                </article>
                <div class="mini-list">
                  <article class="held-row" *ngFor="let item of room.held">
                    <span class="held-type">{{ item.type }}</span>
                    <span class="held-copy">{{ item.title }}</span>
                  </article>
                </div>
              </div>
            </ng-template>

            <ng-template #richtingHome>
              <div class="scroll">
                <div class="astra-lead">Richting</div>
                <div class="astra-grid">
                  <button class="astra-card" type="button" *ngFor="let astra of astras()" (click)="openAstra(astra.id)">
                    <span class="star">★</span>
                    <div class="astra-anchor">{{ astra.title }}</div>
                    <div class="astra-label">{{ astra.label }}</div>
                  </button>
                  <button class="astra-card add" type="button" (click)="openNewAstra()">
                    <span class="star">＋</span>
                    <div class="astra-anchor">nieuwe Astra aanmaken</div>
                    <div class="astra-label">handmatig toevoegen</div>
                  </button>
                </div>

                <div class="meaning-divider">
                  <span class="line"></span>
                  <span class="label">wat Ahastra bewaart</span>
                  <span class="line"></span>
                </div>

                <div class="room-list">
                  <button class="room-card" type="button" *ngFor="let room of rooms()" [ngClass]="toneClass(room.tone)" (click)="openRoom(room.id)">
                    <div class="room-top">
                      <span class="room-dot"></span>
                      <span class="room-label">{{ room.label }}</span>
                    </div>
                    <div class="room-sense">{{ room.sense }}</div>
                  </button>
                </div>
              </div>
            </ng-template>
          </section>

          <section class="screen" *ngIf="screen() === 'jij'" data-screenshot="jij">
            <div class="scroll" *ngIf="activeProfileSection() as section; else jijHome">
              <div class="screen-toolbar">
                <button class="back-button" type="button" (click)="selectedProfileId.set(null)"><span>terug naar jij</span></button>
              </div>
              <div class="cells-lead">Jij</div>
              <article class="detail-card">
                <span class="context-dot"></span>
                <div class="detail-body">
                  <h1 class="detail-title">{{ section.title }}</h1>
                  <div class="detail-meta">{{ section.status }}</div>
                  <p class="detail-copy" *ngFor="let line of section.lines">{{ line }}</p>
                  <div class="detail-actions" *ngIf="section.id === 'profile'">
                    <button type="button" class="secondary-pill" (click)="toggleDeveloperMode()">Developer {{ developerMode() ? 'uitzetten' : 'aanzetten' }}</button>
                    <button type="button" class="start-pill" *ngIf="developerMode()" (click)="openDeveloperTools()">Open Developer Tools</button>
                  </div>
                  <div class="detail-actions" *ngIf="section.id === 'calendar'">
                    <button type="button" class="start-pill" *ngIf="calendarConnection()?.canConnect" (click)="connectCalendar()">Verbinden</button>
                    <button type="button" class="secondary-pill" (click)="importCalendar()">Agenda ophalen</button>
                    <button type="button" class="secondary-pill" *ngIf="calendarConnection()?.connected" (click)="disconnectCalendar()">Loskoppelen</button>
                  </div>
                  <div class="detail-actions" *ngIf="section.id === 'developer' && developerMode()">
                    <button type="button" class="secondary-pill" (click)="advanceDevClock(1)">Advance +1d</button>
                    <button type="button" class="secondary-pill" (click)="advanceDevClock(3)">+3d</button>
                    <button type="button" class="secondary-pill" (click)="advanceDevClock(7)">+7d</button>
                    <button type="button" class="secondary-pill" (click)="advanceDevTime(60)">+1h</button>
                    <button type="button" class="secondary-pill" (click)="advanceDevTime(240)">+4h</button>
                    <button type="button" class="secondary-pill" (click)="restoreActualDate()">Restore actual date</button>
                    <button type="button" class="secondary-pill" (click)="rerunClassification()">Re-run classification</button>
                    <button type="button" class="secondary-pill" (click)="recalculatePriorities()">Recalculate priorities</button>
                    <button type="button" class="start-pill" (click)="resetTestWorld()">Reset Test World</button>
                  </div>
                </div>
              </article>
            </div>

            <ng-template #jijHome>
              <div class="scroll">
                <div class="cells-lead">Jij</div>
                <div class="setting-list">
                  <button class="setting-card" type="button" *ngFor="let section of profileSections()" (click)="openProfile(section.id)">
                    <span class="context-dot"></span>
                    <span class="setting-body">
                      <span class="setting-title">{{ section.title }}</span>
                      <span class="setting-status">{{ section.status }}</span>
                    </span>
                  </button>
                </div>
              </div>
            </ng-template>
          </section>

          <section class="screen" *ngIf="screen() === 'debug' && !environment.production && !mvp1TestMode" data-screenshot="debug">
            <div class="scroll">
              <button class="back-button" type="button" (click)="show('ahastra')"><span>terug naar Ahastra</span></button>
              <div class="home-lead now">Dev validatie</div>
              <article class="detail-card" *ngFor="let row of debugRows()">
                <span class="context-dot"></span>
                <div class="detail-body">
                  <h1 class="detail-title">{{ row.title }}</h1>
                  <div class="detail-meta"><span>score {{ row.score }}</span><span>· {{ row.bucket }}</span><span>· {{ row.chosen ? 'gekozen' : 'niet gekozen' }}</span></div>
                  <p class="detail-copy">{{ row.reason }}</p>
                  <p class="detail-copy">Factoren: {{ row.factors.length ? row.factors.join(', ') : 'geen extra factoren' }}</p>
                </div>
              </article>
            </div>
          </section>

          <div class="flow-sheet" *ngIf="inputFlow() === 'add'">
            <div class="sheet-header">
              <h2 class="flow-title">Zeg het maar</h2>
              <button type="button" class="sheet-close" aria-label="Sluiten" (click)="closeInputFlow()">×</button>
            </div>
            <input
              class="flow-input"
              type="text"
              aria-label="Zeg het maar"
              [value]="draftText()"
              (input)="draftText.set($any($event.target).value)"
              (keydown.enter)="quickSaveDraft()"
              placeholder="Zeg het maar."
            />
            <div class="proposal-edit-group">
              <span class="proposal-edit-label">Wanneer</span>
              <div class="proposal-edit-actions">
                <button type="button" [ngClass]="proposalChoiceClass(selectedTiming() === 'today')" (click)="setDraftTiming('today')">Vandaag</button>
                <button type="button" [ngClass]="proposalChoiceClass(selectedTiming() === 'tonight')" (click)="setDraftTiming('tonight')">Vanavond</button>
                <button type="button" [ngClass]="proposalChoiceClass(selectedTiming() === 'tomorrow')" (click)="setDraftTiming('tomorrow')">Morgen</button>
                <button type="button" [ngClass]="proposalChoiceClass(selectedTiming() === 'this_week')" (click)="setDraftTiming('this_week')">Deze week</button>
                <button type="button" [ngClass]="proposalChoiceClass(selectedTiming() === 'next_week')" (click)="setDraftTiming('next_week')">Volgende week</button>
              </div>
            </div>
            <div class="proposal-edit-group">
              <span class="proposal-edit-label">Zelf kiezen</span>
              <div class="manual-time-grid">
                <input class="flow-input" type="date" aria-label="Datum" [value]="manualDate()" (input)="setManualDate($any($event.target).value)" />
                <input class="flow-input" type="time" aria-label="Tijd" [value]="manualTime()" (input)="setManualTime($any($event.target).value)" />
              </div>
            </div>
            <div class="two-actions proposal-save-row">
              <button type="button" class="start-pill" [disabled]="savingDraft()" (click)="quickSaveDraft()">Opslaan</button>
              <button type="button" class="secondary-pill" (click)="openTimingStep(draftText())">Meer opties</button>
              <button type="button" class="secondary-pill" (click)="closeInputFlow()">Later</button>
            </div>
          </div>

          <div class="flow-sheet" *ngIf="inputFlow() === 'when'">
            <div class="sheet-header">
              <h2 class="flow-title">Wanneer?</h2>
              <button type="button" class="sheet-close" aria-label="Sluiten" (click)="closeInputFlow()">×</button>
            </div>
            <div class="sheet-nav"><button type="button" class="sheet-back" (click)="flowBack()"><span>terug</span></button></div>
            <p class="detail-copy">{{ draftText() }}</p>
            <div class="two-actions">
              <button type="button" class="start-pill" (click)="chooseTiming('now')">Nu</button>
              <button type="button" class="secondary-pill" (click)="chooseTiming('today')">Vandaag</button>
              <button type="button" class="secondary-pill" (click)="chooseTiming('tonight')">Vanavond</button>
              <button type="button" class="secondary-pill" (click)="chooseTiming('tomorrow')">Morgen</button>
              <button type="button" class="secondary-pill" (click)="chooseTiming('this_week')">Deze week</button>
              <button type="button" class="secondary-pill" (click)="chooseTiming('next_week')">Volgende week</button>
              <button type="button" class="secondary-pill" (click)="chooseTiming('manual')">Zelf kiezen</button>
              <button type="button" class="secondary-pill" (click)="chooseTiming('later')">Later</button>
            </div>
          </div>

          <div class="flow-sheet" *ngIf="inputFlow() === 'priority'">
            <div class="sheet-header">
              <h2 class="flow-title">Prioriteit?</h2>
              <button type="button" class="sheet-close" aria-label="Sluiten" (click)="closeInputFlow()">×</button>
            </div>
            <div class="sheet-nav"><button type="button" class="sheet-back" (click)="flowBack()"><span>terug</span></button></div>
            <p class="detail-copy">{{ draftText() }}</p>
            <div class="two-actions">
              <button type="button" class="secondary-pill" (click)="choosePriority('low')">Laag</button>
              <button type="button" class="start-pill" (click)="choosePriority('medium')">Medium</button>
              <button type="button" class="secondary-pill" (click)="choosePriority('high')">Hoog</button>
            </div>
          </div>

          <div class="flow-sheet" *ngIf="inputFlow() === 'repeat'">
            <div class="sheet-header">
              <h2 class="flow-title">Herhaalt dit?</h2>
              <button type="button" class="sheet-close" aria-label="Sluiten" (click)="closeInputFlow()">×</button>
            </div>
            <div class="sheet-nav"><button type="button" class="sheet-back" (click)="flowBack()"><span>terug</span></button></div>
            <p class="detail-copy">{{ draftText() }}</p>
            <div class="two-actions">
              <button type="button" class="start-pill" (click)="chooseRecurrence('none')">Nee</button>
              <button type="button" class="secondary-pill" (click)="chooseRecurrence('daily')">Elke dag</button>
              <button type="button" class="secondary-pill" (click)="chooseRecurrence('weekly')">Elke week</button>
              <button type="button" class="secondary-pill" (click)="chooseRecurrence('monthly')">Elke maand</button>
            </div>
          </div>

          <div class="flow-sheet" *ngIf="inputFlow() === 'speech'">
            <div class="sheet-header">
              <h2 class="flow-title">Ik hoor:</h2>
              <button type="button" class="sheet-close" aria-label="Sluiten" (click)="closeInputFlow()">×</button>
            </div>
            <div class="sheet-nav"><button type="button" class="sheet-back" (click)="flowBack()"><span>terug</span></button></div>
            <p class="detail-copy">{{ draftText() }}</p>
            <div class="two-actions">
              <button type="button" class="start-pill" (click)="confirmSpeech()">Opslaan</button>
              <button type="button" class="secondary-pill" (click)="inputFlow.set('add')">Aanpassen</button>
            </div>
          </div>

          <div class="flow-sheet" *ngIf="inputFlow() === 'proposal' && proposal() as proposed">
            <div class="sheet-header">
              <h2 class="flow-title">Ahastra stelt voor</h2>
              <button type="button" class="sheet-close" aria-label="Sluiten" (click)="closeInputFlow()">×</button>
            </div>
            <div class="sheet-nav"><button type="button" class="sheet-back" (click)="flowBack()"><span>terug</span></button></div>
            <div class="proposal-grid" *ngIf="!mvp1TestMode; else mvp1Proposal">
              <div class="proposal-row"><span>Ruimte</span><strong>{{ proposed.room }}</strong></div>
              <div class="proposal-row"><span>Astra</span><strong>{{ proposed.astra }}</strong></div>
              <div class="proposal-row"><span>Timing</span><strong>{{ proposed.timing }}</strong></div>
              <div class="proposal-row"><span>Aandacht</span><strong>{{ proposed.attention }}</strong></div>
            </div>
            <ng-template #mvp1Proposal>
              <p class="detail-copy">{{ proposed.receipt }}</p>
              <div class="proposal-row"><span>Wanneer</span><strong>{{ proposed.timing }}</strong></div>
              <div class="proposal-row"><span>Prioriteit</span><strong>{{ proposed.priority ?? 'Medium' }}</strong></div>
              <div class="proposal-row" *ngIf="proposed.recurrence"><span>Herhaling</span><strong>{{ proposed.recurrence }}</strong></div>
            </ng-template>
            <div class="proposal-edit-group">
              <span class="proposal-edit-label">Titel</span>
              <input class="flow-input" type="text" aria-label="Titel" [value]="proposed.title" (input)="updateProposalTitle($any($event.target).value)" />
            </div>
            <div class="proposal-edit-group">
              <span class="proposal-edit-label">Beste startmomenten</span>
              <button type="button" class="proposal-time-option" *ngFor="let option of proposed.timeOptions" [class.active]="proposed.selectedTimeOptionId === option.id" (click)="selectTimeOption(option)">
                <strong>{{ option.label }}</strong>
                <small>{{ option.reason }}</small>
              </button>
              <button type="button" class="secondary-pill" (click)="updateProposalTiming('manual')">Handmatig aanpassen</button>
            </div>
            <div class="proposal-edit-group">
              <span class="proposal-edit-label">Datum en starttijd</span>
              <div class="manual-time-grid">
                <input class="flow-input" type="date" aria-label="Datum" [value]="proposalDate(proposed)" (input)="updateProposalDate($any($event.target).value)" />
                <input class="flow-input" type="time" aria-label="Starttijd" [value]="proposalTime(proposed)" (input)="updateProposalTime($any($event.target).value)" />
              </div>
            </div>
            <div class="proposal-edit-group">
              <span class="proposal-edit-label">Brain Cell</span>
              <div class="proposal-edit-actions">
                <button type="button" *ngFor="let cell of brainCells()" [ngClass]="proposalChoiceClass(proposed.brainCellId === cell.id)" (click)="updateProposalBrainCell(cell.id)">{{ cell.label }}</button>
              </div>
            </div>
            <div class="proposal-edit-group">
              <span class="proposal-edit-label">Astra (alleen handmatig)</span>
              <div class="proposal-edit-actions">
                <button type="button" [ngClass]="proposalChoiceClass(!proposed.astraId)" (click)="updateProposalAstra(null)">Geen Astra</button>
                <button type="button" *ngFor="let astra of astraRecords()" [ngClass]="proposalChoiceClass(proposed.astraId === astra.id)" (click)="updateProposalAstra(astra.id)">{{ directionTitle(astra.text) }}</button>
              </div>
            </div>
            <div class="proposal-edit-group">
              <span class="proposal-edit-label">Prioriteit aanpassen</span>
              <div class="proposal-edit-actions">
                <button type="button" [ngClass]="proposalChoiceClass(proposed.userPriority === 'low')" (click)="updateProposalPriority('low')">Laag</button>
                <button type="button" [ngClass]="proposalChoiceClass(proposed.userPriority === 'medium')" (click)="updateProposalPriority('medium')">Medium</button>
                <button type="button" [ngClass]="proposalChoiceClass(proposed.userPriority === 'high')" (click)="updateProposalPriority('high')">Hoog</button>
              </div>
            </div>
            <div class="two-actions proposal-save-row">
              <button type="button" class="start-pill" (click)="confirmProposal()">Opslaan</button>
            </div>
          </div>

          <div class="flow-sheet" *ngIf="helpChoiceOpen()">
            <div class="sheet-header">
              <h2 class="flow-title">Wat heb je nodig?</h2>
              <button type="button" class="sheet-close" aria-label="Sluiten" (click)="helpChoiceOpen.set(false)">×</button>
            </div>
            <p class="detail-copy">Een kleine nudge om te beginnen, of even toestemming om uit te schakelen.</p>
            <div class="two-actions">
              <button type="button" class="start-pill" (click)="chooseHelpNudge()">Kleine nudge</button>
              <button type="button" class="secondary-pill" (click)="chooseHelpRest()">Even rust</button>
            </div>
          </div>

          <div class="flow-sheet" *ngIf="restTips().length">
            <div class="sheet-header">
              <h2 class="flow-title">Even rust</h2>
              <button type="button" class="sheet-close" aria-label="Sluiten" (click)="closeRestTips()">×</button>
            </div>
            <div class="mini-list">
              <article class="mini-row" *ngFor="let tip of restTips()">
                <span class="held-type">rust</span>
                <span class="held-copy">{{ tip }}</span>
              </article>
            </div>
            <div class="two-actions">
              <button type="button" class="start-pill" (click)="closeRestTips()">Terug</button>
            </div>
          </div>

          <div class="flow-sheet day-done-sheet" *ngIf="dayDoneOpen()" data-screenshot="day-done">
            <div class="sheet-header">
              <span></span>
              <button type="button" class="sheet-close" aria-label="Sluiten" (click)="closeDayDone()">×</button>
            </div>
            <div class="day-done-animation" aria-hidden="true">
              <span class="day-done-ring"></span>
              <span class="day-done-ring two"></span>
              <span class="day-done-star main">✦</span>
              <span class="day-done-star one">✧</span>
              <span class="day-done-star two">✦</span>
              <span class="day-done-star three">✧</span>
            </div>
            <h2 class="flow-title">Vandaag is rond.</h2>
            <div class="day-done-summary" *ngIf="dayDoneSummaryVisible()">
              <div class="mini-list" *ngIf="completedToday().length">
                <article class="mini-row" *ngFor="let title of completedToday()">
                  <span class="held-type">klaar</span>
                  <span class="held-copy">{{ title }}</span>
                </article>
              </div>
              <div class="mini-list" *ngIf="skippedToday().length">
                <article class="mini-row" *ngFor="let title of skippedToday()">
                  <span class="held-type">verplaatst</span>
                  <span class="held-copy">{{ title }}</span>
                </article>
              </div>
              <p class="detail-copy" *ngIf="skippedToday().length">Verplaatste acties staan klaar op hun volgende moment.</p>
            </div>
            <p class="day-done-final" *ngIf="!dayDoneSummaryVisible()">Sluit Ahastra en de rest van de dag is van jou.</p>
            <div class="two-actions">
              <button type="button" class="start-pill" (click)="closeDayDone()">Sluit Ahastra</button>
            </div>
          </div>

          <div class="flow-sheet completion-pop" *ngIf="completionFeedback() as feedback" data-screenshot="task-complete">
            <div class="completion-orb" aria-hidden="true">✓</div>
            <h2 class="completion-title">{{ feedback.message }}</h2>
            <p class="detail-copy">{{ feedback.title }} hoeft nu niet meer in je hoofd.</p>
          </div>

          <div class="flow-sheet top-sheet" *ngIf="skipAction() as action">
            <div class="sheet-header">
              <h2 class="flow-title">Skip</h2>
              <button type="button" class="sheet-close" aria-label="Sluiten" (click)="cancelSkip()">×</button>
            </div>
            <p class="replan-note">Wat past beter voor “{{ action.title }}”?</p>
            <p class="replan-note" *ngIf="action.calendar">Deze afspraak blijft zichtbaar waar hij vast staat.</p>
            <div class="two-actions">
              <button type="button" class="start-pill" (click)="confirmSkip(action.id, 'later')">Later herplannen</button>
              <button type="button" class="secondary-pill" (click)="confirmSkip(action.id, 'full')">Volledig skippen</button>
              <button type="button" class="secondary-pill" (click)="cancelSkip()">Nu toch doen</button>
            </div>
          </div>

          <div class="flow-sheet top-sheet" *ngIf="resetOpen()">
            <div class="sheet-header">
              <h2 class="flow-title">Test reset</h2>
              <button type="button" class="sheet-close" aria-label="Sluiten" (click)="resetOpen.set(false)">×</button>
            </div>
            <p class="reset-copy">Voor Bram en Rianne. Dit reset alleen de lokale testdata in .ahastra-dev/db.json.</p>
            <div class="two-actions">
              <button type="button" class="start-pill" [disabled]="resetBusy()" (click)="resetLocalData('demo')">Demo opnieuw starten</button>
              <button type="button" class="secondary-pill" [disabled]="resetBusy()" (click)="resetLocalData('empty')">Leeg beginnen</button>
            </div>
          </div>

          <div class="flow-sheet top-sheet" *ngIf="newAstraOpen()">
            <div class="sheet-header">
              <h2 class="flow-title">Nieuwe Astra</h2>
              <button type="button" class="sheet-close" aria-label="Sluiten" (click)="newAstraOpen.set(false)">×</button>
            </div>
            <input class="flow-input" type="text" aria-label="Nieuwe Astra" [value]="newAstraText()" (input)="newAstraText.set($any($event.target).value)" placeholder="Welke richting?" />
            <div class="proposal-edit-group">
              <span class="proposal-edit-label">Doeldatum</span>
              <div class="manual-date-grid">
                <input class="flow-input" inputmode="numeric" maxlength="2" aria-label="Dag" [value]="newAstraDay()" (input)="newAstraDay.set($any($event.target).value)" placeholder="dd" />
                <input class="flow-input" inputmode="numeric" maxlength="2" aria-label="Maand" [value]="newAstraMonth()" (input)="newAstraMonth.set($any($event.target).value)" placeholder="mm" />
                <input class="flow-input" inputmode="numeric" maxlength="4" aria-label="Jaar" [value]="newAstraYear()" (input)="newAstraYear.set($any($event.target).value)" placeholder="jjjj" />
              </div>
            </div>
            <div class="proposal-edit-actions">
              <button type="button" *ngFor="let cell of brainCells()" [ngClass]="proposalChoiceClass((newAstraCellId() || brainCells()[0]?.id) === cell.id)" (click)="newAstraCellId.set(cell.id)">{{ cell.label }}</button>
            </div>
            <div class="two-actions">
              <button type="button" class="start-pill" (click)="createNewAstra()">Toevoegen</button>
              <button type="button" class="secondary-pill" (click)="newAstraOpen.set(false)">Annuleren</button>
            </div>
          </div>

          <div class="input-bar-wrap">
            <form class="input-bar" aria-label="Vaste plek om iets neer te leggen" (submit)="submitInputForm($event)" (click)="focusCaptureInput(captureInput)">
              <button class="round-icon" type="button" aria-label="Spreek iets in" (click)="startSpeech(); $event.stopPropagation()">🎙</button>
              <input
                #captureInput
                class="input-field"
                type="text"
                aria-label="Zeg het maar"
                name="capture"
                autocomplete="off"
                autocapitalize="sentences"
                enterkeyhint="done"
                [value]="inputText()"
                (input)="updateCaptureText($any($event.target).value)"
                (paste)="capturePaste(captureInput)"
                (keydown.enter)="submitInputForm($event)"
                placeholder="Zeg het maar."
              />
            </form>
          </div>

          <nav class="bottom-nav" aria-label="Hoofdnavigatie" *ngIf="!mvp1TestMode">
            <button type="button" [class.active]="screen() === 'ahastra'" (click)="show('ahastra')">
              <span class="nav-icon">⌂</span>
              <span>Ahastra</span>
            </button>
            <button type="button" [class.active]="screen() === 'richting'" (click)="show('richting')">
              <span class="nav-icon">★</span>
              <span>Richting</span>
            </button>
            <button type="button" [class.active]="screen() === 'jij'" (click)="show('jij')">
              <span class="nav-icon">●</span>
              <span>Jij</span>
            </button>
          </nav>
        </article>
      </main>
    </ion-content>
  `,
})
export class ShellPageComponent implements OnDestroy {
  readonly screen = signal<CoreScreen>(this.initialScreen());
  readonly helpMode = signal(false);
  readonly selectedActionId = signal<string | null>(null);
  readonly whyActionId = signal<string | null>(null);
  readonly skipActionId = signal<string | null>(null);
  readonly returnDays = signal<SafeReturnDays | null>(null);
  readonly actionStepsOpen = signal(false);
  readonly startedActionIds = signal<ReadonlySet<string>>(new Set<string>());
  readonly promotedActionIds = signal<ReadonlySet<string>>(new Set<string>());
  readonly completedStepIds = signal<ReadonlySet<string>>(new Set<string>());
  readonly microStepActionIds = signal<ReadonlySet<string>>(new Set<string>());
  readonly hiddenItemsOpen = signal(false);
  readonly hiddenViewMode = signal<HiddenViewMode>("day-set");
  readonly reliefMessage = signal<string | null>(null);
  readonly safeReturnPromptDays = signal<SafeReturnDays | null>(null);
  readonly startedCheckInVisible = signal(false);
  readonly helpChoiceOpen = signal(false);
  readonly dayDoneOpen = signal(false);
  readonly dayDoneSummaryVisible = signal(false);
  readonly skipConsequenceAccepted = signal(false);
  readonly resetOpen = signal(false);
  readonly resetBusy = signal(false);
  readonly developerUnlocked = signal(this.initialDeveloperUnlocked());
  readonly developerMode = signal(this.initialDeveloperMode());
  readonly devDayOffset = signal(this.initialDevDayOffset());
  readonly devMinuteOffset = signal(this.initialDevMinuteOffset());
  readonly devTapCount = signal(0);
  readonly newAstraOpen = signal(false);
  readonly newAstraText = signal("");
  readonly newAstraCellId = signal<string | null>(null);
  readonly newAstraDay = signal("");
  readonly newAstraMonth = signal("");
  readonly newAstraYear = signal("");
  readonly completedToday = signal<readonly string[]>([]);
  readonly skippedToday = signal<readonly string[]>([]);
  readonly restTips = signal<readonly string[]>([]);
  readonly inputText = signal("");
  readonly draftText = signal("");
  readonly selectedTiming = signal<TimingChoice | null>(null);
  readonly selectedPriority = signal<PriorityChoice | null>(null);
  readonly selectedRecurrence = signal<RecurrenceChoice>("none");
  readonly manualDate = signal("");
  readonly manualTime = signal("");
  readonly savingDraft = signal(false);
  readonly inputFlow = signal<InputFlow>("idle");
  readonly proposal = signal<Proposal | null>(null);
  readonly completionFeedback = signal<CompletionFeedback | null>(null);
  readonly selectedAstraId = signal<string | null>(null);
  readonly selectedRoomId = signal<string | null>(null);
  readonly selectedProfileId = signal<string | null>(null);
  readonly priorities = signal<PriorityItem[]>([]);
  readonly debugRows = signal<readonly PriorityDebugItem[]>([]);
  readonly daySetItems = signal<readonly PriorityItem[]>([]);
  readonly overviewItems = signal<readonly PriorityItem[]>([]);
  readonly calendarConnection = signal<CalendarConnection | null>(null);
  readonly hiddenExpandedGroups = signal<ReadonlySet<HiddenActionGroup["id"]>>(new Set(["today", "tomorrow", "this_week", "next_week", "later"]));

  private readonly api = inject(ApiClientService);
  private readonly pseudonymId = signal<string | null>(null);
  readonly brainCells = signal<readonly BrainCell[]>([]);
  readonly astraRecords = signal<readonly AstraGoal[]>([]);
  private readonly actions = signal<readonly UserAction[]>([]);
  private readonly dayPlan = signal<DayPlan | null>(null);
  private readonly todaySet = signal<TodaySet | null>(null);
  readonly reliefFading = signal(false);
  private reliefFadeTimer: ReturnType<typeof globalThis.setTimeout> | null = null;
  private reliefClearTimer: ReturnType<typeof globalThis.setTimeout> | null = null;
  private dayDoneFadeTimer: ReturnType<typeof globalThis.setTimeout> | null = null;
  private completionTimer: ReturnType<typeof globalThis.setTimeout> | null = null;
  private speechRecognition: SpeechRecognitionLike | null = null;

  readonly environment = environment;
  readonly mvp1TestMode = environment.mvp1TestMode;
  private readonly activeNow = signal(this.computeActiveNowISO(new Date()));
  private readonly currentDay = signal(this.activeDayFor(this.activeNow()));
  readonly currentTimeLabel = computed(() => this.activeNow().slice(11, 16));
  readonly activeAhastraLabel = computed(() => (this.developerMode() ? this.activeNow().replace("T", " ").slice(0, 16) : this.activeNow().slice(0, 10)));
  private clockTimer: ReturnType<typeof globalThis.setInterval> | null = null;

  readonly todaySetRankableIds = computed(() => new Set(this.todaySet()?.rankableIds ?? []));
  readonly orderedPriorities = computed<readonly PriorityItem[]>(() => this.orderPriorityItems(this.priorities()));
  readonly visiblePriorities = computed<readonly PriorityItem[]>(() => {
    if (this.helpMode() || this.returnDays()) return this.orderedPriorities().filter((item) => this.actionForItemIsOpen(item)).slice(0, 2);
    return this.orderedPriorities().filter((item) => this.homeSelectableItem(item)).slice(0, 2);
  });
  readonly todayRestItems = computed<readonly PriorityItem[]>(() => {
    const visibleIds = new Set(this.visiblePriorities().map((item) => item.id));
    return this.orderPriorityItems(this.daySetItems()).filter((item) => !visibleIds.has(item.id) && this.homeSelectableItem(item));
  });
  readonly hiddenActionItems = computed<readonly PriorityItem[]>(() => this.orderPriorityItems(this.hiddenViewMode() === "overview" ? this.overviewItems() : this.todayRestItems()));
  readonly hiddenActionGroups = computed<readonly HiddenActionGroup[]>(() => this.buildHiddenActionGroups(this.hiddenActionItems()));
  readonly moreHiddenCount = computed(() => this.todayRestItems().length);

  readonly profileSections = computed<readonly ProfileSection[]>(() => {
    const calendar = this.calendarConnection();
    const sections: ProfileSection[] = [
      {
        id: "profile",
        title: "Profiel",
        status: "Bram · persoonlijk profiel",
        lines: ["Basisprofiel voor deze klikbare versie.", "Naam, dagritme en voorkeurstaal komen hier samen."],
      },
      {
        id: "calendar",
        title: "Google Calendar",
        status: calendar?.connected ? "Gekoppeld" : calendar?.canConnect ? "Klaar om te koppelen" : "Google Calendar is nog niet geconfigureerd",
        lines: calendar?.connected
          ? ["Agenda-items kunnen nu meewegen in wat vandaag zichtbaar wordt.", "Ahastra leest alleen opgehaalde agenda-items."]
          : ["De koppeling staat klaar zodra de beta OAuth-configuratie actief is.", "Daarna kun je hier je persoonlijke Google Calendar verbinden."],
      },
      {
        id: "notifications",
        title: "Meldingen",
        status: "Zacht en beperkt",
        lines: ["Alleen terugkomen wanneer het helpt.", "Geen druk, geen rode tellers, geen constante ruis."],
      },
      {
        id: "preferences",
        title: "Ahastra voorkeuren",
        status: "Streng op inhoud, rustig in gebruik",
        lines: ["Prioritering staat nu op gebalanceerd.", "Overload gebruikt een aparte, smallere afweging."],
      },
      {
        id: "privacy",
        title: "Privacy",
        status: "Lokaal bewaren",
        lines: ["Deze lokale MVP bewaart acties in de lokale API-opslag.", "Live koppelingen blijven apart zichtbaar."],
      },
    ];
    return sections;
  });

  constructor() {
    this.api.setActiveNowProvider(() => this.activeNowISO());
    this.refreshLocalClock();
    this.clockTimer = globalThis.setInterval(() => void this.handleClockTick(), 30_000);
    void this.initializeIntelligence();
  }

  ngOnDestroy(): void {
    if (this.clockTimer) globalThis.clearInterval(this.clockTimer);
    if (this.reliefFadeTimer) globalThis.clearTimeout(this.reliefFadeTimer);
    if (this.reliefClearTimer) globalThis.clearTimeout(this.reliefClearTimer);
    if (this.dayDoneFadeTimer) globalThis.clearTimeout(this.dayDoneFadeTimer);
    if (this.completionTimer) globalThis.clearTimeout(this.completionTimer);
    this.speechRecognition?.stop();
    this.speechRecognition = null;
  }

  readonly astras = computed<readonly AstraDirection[]>(() => {
    const cells = this.brainCells();
    const actions = this.actions();
    return this.astraRecords()
      .filter((astra) => astra.active !== false)
      .slice()
      .sort((left, right) => left.rank - right.rank)
      .map((astra) => {
        const cell = cells.find((item) => item.id === astra.brainCellId);
        const linkedActions = actions.filter((action) => action.astraId === astra.id);
        return {
          id: astra.id,
          title: this.directionTitle(astra.text),
          label: cell?.label ?? "Richting",
          description: astra.text,
          why: "Deze richting helpt Ahastra losse acties op hun plek te houden.",
          recent: this.directionRecent(linkedActions),
          rooms: [cell?.label ?? "Persoonlijk"],
          actions: this.directionActions(linkedActions),
        };
      });
  });

  readonly rooms = computed<readonly BrainCellRoom[]>(() =>
    this.brainCells()
      .filter((cell) => cell.active !== false)
      .map((cell) => ({
        id: cell.id,
        label: cell.label,
        sense: this.roomSense(cell.label),
        tone: this.toneForRoom(cell.label),
        held: this.roomHeld(cell.id),
      })),
  );

  show(screen: CoreScreen): void {
    if (this.mvp1TestMode && screen !== "ahastra") screen = "ahastra";
    if (screen === "debug" && (environment.production || this.mvp1TestMode)) screen = "ahastra";
    this.screen.set(screen);
    this.helpMode.set(false);
    this.selectedActionId.set(null);
    this.skipActionId.set(null);
    this.skipConsequenceAccepted.set(false);
    this.resetOpen.set(false);
    this.whyActionId.set(null);
    this.hiddenItemsOpen.set(false);
    this.closeDirectionDetail();
    this.selectedProfileId.set(null);
    globalThis.history.replaceState(null, "", `#${screen}`);
    void this.logUsage("page_loaded", { view: screen });
    if (screen === "debug") void this.refreshDebug();
  }

  openAction(actionId: string): void {
    this.selectedActionId.set(actionId);
    this.actionStepsOpen.set(false);
    this.skipActionId.set(null);
    this.showRelief(null);
  }

  closeAction(): void {
    this.selectedActionId.set(null);
    this.skipActionId.set(null);
    this.skipConsequenceAccepted.set(false);
    this.actionStepsOpen.set(false);
  }

  activeAction(): PriorityItem | null {
    return this.actionForId(this.selectedActionId());
  }

  skipAction(): PriorityItem | null {
    return this.actionForId(this.skipActionId());
  }

  toggleWhy(actionId: string): void {
    this.whyActionId.update((current) => (current === actionId ? null : actionId));
  }

  whyOpen(actionId: string): boolean {
    return this.whyActionId() === actionId;
  }

  async enterOverload(): Promise<void> {
    this.helpMode.set(true);
    this.actionStepsOpen.set(false);
    this.selectedActionId.set(null);
    this.skipActionId.set(null);
    await this.refreshPlan("overload");
  }

  async exitOverload(): Promise<void> {
    this.helpMode.set(false);
    this.actionStepsOpen.set(false);
    await this.refreshPlan();
  }

  async reweighNow(): Promise<void> {
    this.startedCheckInVisible.set(false);
    this.helpChoiceOpen.set(false);
    this.helpMode.set(false);
    await this.refreshPlan();
    this.showRelief("Ahastra kijkt opnieuw naar vandaag.", 2600);
  }

  openHelpChoice(): void {
    this.restTips.set([]);
    this.helpChoiceOpen.set(true);
  }

  async chooseHelpNudge(): Promise<void> {
    this.helpChoiceOpen.set(false);
    this.restTips.set([]);
    this.skipActionId.set(null);
    this.selectedActionId.set(null);
    const top = this.priorities()[0];
    if (top?.steps.length) this.microStepActionIds.update((ids) => new Set([...ids, top.id]));
    this.actionStepsOpen.set(Boolean(top?.steps.length));
    this.helpMode.set(true);
  }

  chooseHelpRest(): void {
    this.helpChoiceOpen.set(false);
    this.helpMode.set(false);
    this.restTips.set(this.randomRecoveryTips());
  }

  closeRestTips(): void {
    this.restTips.set([]);
  }

  acceptStartedCheckIn(): void {
    this.startedCheckInVisible.set(false);
  }

  savedCardText(): string {
    const hidden = this.moreHiddenCount();
    if (hidden > 0) return `Rest van vandaag (${hidden}). Als die gedaan zijn, is vandaag goed genoeg.`;
    return "Dit is alles. Als deze klaar zijn, is je dag klaar.";
  }

  safeReturnMessage(): string | null {
    return this.returnDays() ? "We beginnen met wat nu telt." : null;
  }

  firstStepTitle(action: PriorityItem): string {
    return this.currentMicroStep(action) ?? action.steps[0] ?? action.title;
  }

  cardTitle(action: PriorityItem): string {
    if (!this.microStepActionIds().has(action.id)) return action.title;
    return this.currentMicroStep(action) ?? action.title;
  }

  cardContext(action: PriorityItem): string {
    return this.microStepActionIds().has(action.id) && this.currentMicroStep(action) ? action.title : action.room;
  }

  sourceLabel(action: Pick<PriorityItem, "source">): string {
    if (action.source === "calendar") return "calendar";
    if (action.source === "ai") return "ai";
    return "manual";
  }

  sourceIcon(action: Pick<PriorityItem, "source">): string {
    if (action.source === "calendar") return "◷";
    if (action.source === "ai") return "✦";
    return "+";
  }

  cardDateTime(item: PriorityItem): string {
    const action = this.actions().find((candidate) => candidate.id === item.id);
    const value = action?.lockedAt ?? item.timingChip;
    const day = (action?.deferredUntil ?? action?.lockedAt ?? action?.lockedDay ?? action?.dueDate)?.slice(0, 10) ?? this.todayISO();
    const time = action?.lockedAt?.slice(11, 16) ?? (/\d{2}:\d{2}/.exec(value)?.[0]);
    return this.developerMode() && time ? `${day} ${time}` : day;
  }

  ownerLabel(owner: CalendarOwner): string {
    if (owner === "me") return "me";
    if (owner === "partner") return "partner";
    return "shared";
  }

  actionHasAstra(actionId: string): boolean {
    return Boolean(this.actions().find((action) => action.id === actionId)?.astraId ?? this.actionForId(actionId)?.astraId);
  }

  actionLinkedToAstra(actionId: string, astraId: string): boolean {
    return (this.actions().find((action) => action.id === actionId)?.astraId ?? this.actionForId(actionId)?.astraId) === astraId;
  }

  async assignActionAstra(actionId: string, astraId: string | null): Promise<void> {
    const pseudonymId = this.pseudonymId();
    if (!pseudonymId) return;
    await this.withApiError(async () => {
      const assigned = await this.api.assignActionAstra(pseudonymId, actionId, astraId ?? undefined);
      await this.refreshBootstrap();
      if (assigned.dayPlan) {
        this.applyDayPlan(assigned.dayPlan, assigned.todaySet);
        await this.refreshDaySet();
      } else await this.refreshPlan();
      this.showRelief(astraId ? "Astra gekoppeld." : "Astra losgekoppeld.", 2200);
    });
  }

  startAction(actionId: string): void {
    this.startedActionIds.update((ids) => {
      const next = new Set([...ids, actionId]);
      this.persistStartedActions(next);
      return next;
    });
    this.startedCheckInVisible.set(false);
  }

  actionStarted(actionId: string): boolean {
    return this.startedActionIds().has(actionId);
  }

  isPromoted(actionId: string): boolean {
    return this.promotedActionIds().has(actionId);
  }

  promoteAction(actionId: string): void {
    const action = this.actions().find((candidate) => candidate.id === actionId);
    if (!action || action.status !== "open") return;
    this.promotedActionIds.update((ids) => {
      const next = new Set([actionId, ...[...ids].filter((id) => id !== actionId)]);
      this.persistPromotedActions(next);
      return next;
    });
    this.hiddenItemsOpen.set(false);
    this.showRelief("Bovenaan gezet. Jij kiest.", 2200);
  }

  unpromoteAction(actionId: string): void {
    this.clearPromotedAction(actionId);
    this.showRelief("Niet meer bovenaan vastgezet.", 2200);
  }

  async completeCardAction(actionId: string): Promise<void> {
    const action = this.actionForId(actionId);
    if (action && this.microStepActionIds().has(actionId)) {
      const step = this.currentMicroStep(action);
      if (!step) {
        this.openAction(actionId);
        this.showRelief("De actie staat nog open. Rond hem af in de actiekaart.", 2200);
        return;
      }
      this.markStepDone(actionId, step);
      this.showRelief(this.currentMicroStep(action) ? "Stap klaar." : "Alle kleine stappen zijn klaar. De actie staat nog open.", 2200);
      return;
    }
    await this.completeWholeAction(actionId);
  }

  async completeWholeAction(actionId: string): Promise<void> {
    const pseudonymId = this.pseudonymId();
    if (!pseudonymId) return;
    const title = this.actionForId(actionId)?.title;
    await this.withApiError(async () => {
      const completed = await this.api.completeAction(pseudonymId, actionId);
      this.clearStartedAction(actionId);
      this.clearPromotedAction(actionId);
      if (title) this.completedToday.update((items) => [...items, title]);
      this.selectedActionId.set(null);
      this.skipActionId.set(null);
      this.actionStepsOpen.set(false);
      this.helpMode.set(false);
      this.showCompletionFeedback(title ?? "Deze actie", completed.confirmation ?? "Klaar. Je mag dit loslaten.");
      await this.refreshBootstrap();
      if (completed.dayPlan) {
        this.applyDayPlan(completed.dayPlan, completed.todaySet);
        await this.refreshDaySet();
      } else {
        await this.refreshPlan();
      }
      this.showRelief(this.priorities().length ? "Ahastra kijkt opnieuw naar wat nu telt." : null, this.priorities().length ? 2200 : undefined);
      this.maybeOpenDayDone();
    });
  }

  requestSkip(actionId: string): void {
    this.resetOpen.set(false);
    this.skipConsequenceAccepted.set(false);
    this.skipActionId.set(actionId);
  }

  cancelSkip(): void {
    this.skipActionId.set(null);
    this.skipConsequenceAccepted.set(false);
  }

  skipRequiresConsequence(action: PriorityItem): boolean {
    return action.urgent;
  }

  skipConsequence(action: PriorityItem): string {
    return action.urgentConsequence;
  }

  openReset(): void {
    this.skipActionId.set(null);
    this.resetOpen.set(true);
  }

  async resetLocalData(mode: DevResetMode): Promise<void> {
    this.resetBusy.set(true);
    try {
      await this.withApiError(async () => {
        const installSeed = this.localInstallSeed();
        globalThis.localStorage?.setItem(this.devResetModeKey(), mode);
        await this.api.resetDevData(mode, installSeed, this.todayISO());
        this.clearLocalRunState();
        const installation = await this.api.createInstallation(installSeed);
        this.pseudonymId.set(installation.user.pseudonymId);
        this.restoreStartedActions(installation.user.pseudonymId);
        this.restorePromotedActions(installation.user.pseudonymId);
        let bootstrap = await this.refreshBootstrap();
        if (!bootstrap.brainCells.length) {
          await this.api.saveOnboarding(installation.user.pseudonymId);
          bootstrap = await this.refreshBootstrap();
        }
        if (!bootstrap.latestMood) await this.api.saveMood(installation.user.pseudonymId, "prima", "small");
        await this.refreshBootstrap();
        await this.refreshPlan();
        this.resetOpen.set(false);
        this.showRelief(mode === "demo" ? "Demo opnieuw gestart." : "Leeg begonnen. Voeg je eigen acties toe.", 2600);
      });
    } finally {
      this.resetBusy.set(false);
    }
  }

  async confirmSkip(actionId: string, replanFor: "tomorrow" | "later" | "full" = "later"): Promise<void> {
    await this.postponeAction(actionId, replanFor);
  }

  async postponeAction(actionId: string, replanFor: "tomorrow" | "later" | "full" = "later"): Promise<void> {
    const pseudonymId = this.pseudonymId();
    if (!pseudonymId) return;
    const title = this.actionForId(actionId)?.title;
    await this.withApiError(async () => {
      const skipped = await this.api.skipAction(pseudonymId, actionId, replanFor, this.todayISO());
      this.clearStartedAction(actionId);
      this.clearPromotedAction(actionId);
      if (title) this.skippedToday.update((items) => [...items, title]);
      this.selectedActionId.set(null);
      this.skipActionId.set(null);
      this.actionStepsOpen.set(false);
      this.helpMode.set(false);
      await this.refreshBootstrap();
      if (skipped.dayPlan) {
        this.applyDayPlan(skipped.dayPlan, skipped.todaySet);
        await this.refreshDaySet();
        await this.refreshDebug();
      } else {
        await this.refreshPlan();
      }
      this.showRelief(this.priorities().length ? (replanFor === "full" ? "Volledig geskipt." : "Ik herplan dit voor later.") : null, this.priorities().length ? 2600 : undefined);
      this.maybeOpenDayDone();
    });
  }

  async makeActionSmaller(actionId: string): Promise<void> {
    const pseudonymId = this.pseudonymId();
    if (!pseudonymId) return;
    await this.withApiError(async () => {
      const smaller = await this.api.makeSmaller(pseudonymId, actionId);
      this.microStepActionIds.update((ids) => new Set([...ids, actionId]));
      this.actionStepsOpen.set(true);
      if (smaller.action) {
        await this.refreshBootstrap();
        await this.refreshPlan();
      }
      this.showRelief("Kleiner gemaakt. Alleen de eerste stap hoeft nu.", 2600);
    });
  }

  async replanHiddenItem(actionId: string, timing: Extract<TimingChoice, "today" | "tomorrow">): Promise<void> {
    const pseudonymId = this.pseudonymId();
    if (!pseudonymId) return;
    await this.withApiError(async () => {
      const replanned = await this.api.replanAction(pseudonymId, actionId, timing, "high");
      await this.refreshBootstrap();
      if (replanned.dayPlan) {
        this.applyDayPlan(replanned.dayPlan, replanned.todaySet);
        await this.refreshDaySet();
      } else await this.refreshPlan();
      this.showRelief("Ahastra kijkt opnieuw naar vandaag.", 2600);
    });
  }

  async removeAction(actionId: string, scope: "occurrence" | "series" = "occurrence"): Promise<void> {
    const pseudonymId = this.pseudonymId();
    if (!pseudonymId) return;
    await this.withApiError(async () => {
      const removed = await this.api.removeAction(pseudonymId, actionId, scope);
      this.clearStartedAction(actionId);
      this.clearPromotedAction(actionId);
      this.selectedActionId.set(null);
      await this.refreshBootstrap();
      if (removed.dayPlan) {
        this.applyDayPlan(removed.dayPlan, removed.todaySet);
        await this.refreshDaySet();
      } else await this.refreshPlan();
      this.showRelief("Verwijderd.", 2600);
      this.maybeOpenDayDone();
    });
  }

  toggleActionSteps(): void {
    this.actionStepsOpen.update((open) => !open);
  }

  toggleStep(actionId: string, step: string): void {
    const key = `${actionId}:${step}`;
    this.completedStepIds.update((ids) => {
      const next = new Set(ids);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  private markStepDone(actionId: string, step: string): void {
    const key = `${actionId}:${step}`;
    this.completedStepIds.update((ids) => new Set([...ids, key]));
  }

  stepDone(actionId: string, step: string): boolean {
    return this.completedStepIds().has(`${actionId}:${step}`);
  }

  openHiddenItems(): void {
    this.hiddenViewMode.set("day-set");
    this.hiddenItemsOpen.set(true);
  }

  async openOverviewItems(): Promise<void> {
    const pseudonymId = this.pseudonymId();
    if (!pseudonymId) return;
    this.hiddenViewMode.set("overview");
    await this.withApiError(async () => {
      const { items, todaySet } = await this.api.dayOverview(pseudonymId, this.todayISO());
      this.applyTodaySet(todaySet);
      this.overviewItems.set(items.map((item) => this.toPriorityItem(item)).filter((item) => this.isTodayItem(item)));
      this.hiddenItemsOpen.set(true);
    });
  }

  toggleHiddenGroup(groupId: HiddenActionGroup["id"]): void {
    this.hiddenExpandedGroups.update((groups) => {
      const next = new Set(groups);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  }

  hiddenGroupExpanded(groupId: HiddenActionGroup["id"]): boolean {
    return this.hiddenExpandedGroups().has(groupId);
  }

  todayEnoughText(): string {
    const count = this.visiblePriorities().length;
    if (count >= 3) return "Als deze drie gedaan zijn, is vandaag goed genoeg.";
    if (count === 2) return "Als deze twee gedaan zijn, is vandaag goed genoeg.";
    return "Voor nu is dit genoeg.";
  }

  async submitInputForm(event: Event): Promise<void> {
    event.preventDefault();
    await this.proposeFromInput();
  }

  updateCaptureText(value: string): void {
    this.inputText.set(value);
  }

  capturePaste(input: HTMLInputElement): void {
    globalThis.setTimeout(() => this.inputText.set(input.value), 0);
  }

  focusCaptureInput(input: HTMLInputElement): void {
    try {
      input.focus({ preventScroll: true });
    } catch {
      input.focus();
    }
  }

  async proposeFromInput(): Promise<void> {
    await this.captureToProposal(this.inputText());
  }

  startSpeech(): void {
    const existingText = this.inputText().trim();
    if (existingText) {
      void this.captureToProposal(existingText);
      return;
    }
    const Recognition = this.speechRecognitionConstructor();
    if (!Recognition) {
      this.draftText.set("");
      this.inputFlow.set("add");
      this.showRelief("Spraak is nog niet live gekoppeld. Typ het nu even in en druk op enter.", 2600);
      return;
    }
    try {
      this.speechRecognition?.stop();
      const recognition = new Recognition();
      this.speechRecognition = recognition;
      recognition.lang = globalThis.navigator?.language || "nl-NL";
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.maxAlternatives = 1;
      recognition.onresult = (event) => {
        const transcript = event.results[0]?.[0]?.transcript?.trim() ?? "";
        if (!transcript) return;
        this.inputText.set(transcript);
        void this.captureToProposal(transcript);
      };
      recognition.onerror = () => this.showRelief("Spraak lukte niet. Typ het nu even in en druk op enter.", 2600);
      recognition.onend = () => {
        if (this.speechRecognition === recognition) this.speechRecognition = null;
      };
      recognition.start();
      this.showRelief("Ik luister. Spreek je taak rustig in.", 2200);
    } catch {
      this.speechRecognition = null;
      this.showRelief("Spraak lukte niet. Typ het nu even in en druk op enter.", 2600);
    }
  }

  async confirmSpeech(): Promise<void> {
    await this.quickSaveDraft();
  }

  private async captureToProposal(raw: string): Promise<void> {
    const text = raw.trim();
    if (!text) return;
    this.draftText.set(text);
    await this.prepareProposal(text, null, "medium", "none");
  }

  private speechRecognitionConstructor(): SpeechRecognitionConstructor | undefined {
    const speechGlobal = globalThis as typeof globalThis & { SpeechRecognition?: SpeechRecognitionConstructor; webkitSpeechRecognition?: SpeechRecognitionConstructor };
    return speechGlobal.SpeechRecognition ?? speechGlobal.webkitSpeechRecognition;
  }

  openTimingStep(raw: string): void {
    const title = raw.trim();
    if (!title) return;
    this.draftText.set(title);
    this.inputFlow.set("when");
  }

  setDraftTiming(timing: TimingChoice): void {
    this.selectedTiming.set(timing);
    void this.logUsage("date_selected", { selectedDate: this.timingLabel(timing), selectedTime: timing === "tonight" ? "20:00" : undefined, source: "quick_add" });
  }

  setManualDate(value: string): void {
    this.manualDate.set(value);
    this.selectedTiming.set("manual");
    void this.logUsage("date_selected", { selectedDate: value, selectedTime: this.manualTime() || undefined, source: "manual" });
  }

  setManualTime(value: string): void {
    this.manualTime.set(value);
    this.selectedTiming.set("manual");
    void this.logUsage("date_selected", { selectedDate: this.manualDate() || undefined, selectedTime: value, source: "manual" });
  }

  async chooseTiming(timing: TimingChoice): Promise<void> {
    this.setDraftTiming(timing);
    this.inputFlow.set("priority");
  }

  async choosePriority(priority: PriorityChoice): Promise<void> {
    this.selectedPriority.set(priority);
    this.inputFlow.set("repeat");
  }

  async chooseRecurrence(recurrence: RecurrenceChoice): Promise<void> {
    this.selectedRecurrence.set(recurrence);
    await this.prepareProposal(this.draftText(), this.selectedTiming(), this.selectedPriority() ?? "medium", recurrence);
  }

  flowBack(): void {
    const flow = this.inputFlow();
    if (flow === "proposal") this.inputFlow.set("repeat");
    else if (flow === "repeat") this.inputFlow.set("priority");
    else if (flow === "priority") this.inputFlow.set("when");
    else if (flow === "when" || flow === "speech") this.inputFlow.set("add");
    else this.closeInputFlow();
  }

  proposalChoiceClass(active: boolean): string {
    return active ? "start-pill" : "secondary-pill";
  }

  updateProposalTiming(timing: TimingChoice): void {
    const current = this.proposal();
    if (!current) return;
    const priority = current.userPriority ?? this.selectedPriority() ?? "medium";
    const recurrence = current.recurrenceChoice ?? this.selectedRecurrence();
    this.setDraftTiming(timing);
    this.proposal.set(this.withTimingContext(current, timing, priority, recurrence));
  }

  updateProposalPriority(priority: PriorityChoice): void {
    const current = this.proposal();
    if (!current) return;
    const timing = current.userTime ?? this.selectedTiming();
    const recurrence = current.recurrenceChoice ?? this.selectedRecurrence();
    this.selectedPriority.set(priority);
    this.proposal.set(timing ? this.withTimingContext(current, timing, priority, recurrence) : { ...current, userPriority: priority, recurrenceChoice: recurrence });
  }

  updateProposalTitle(title: string): void {
    const current = this.proposal();
    if (!current) return;
    this.proposal.set({ ...current, title });
    this.draftText.set(title);
  }

  selectTimeOption(option: ProposedTimeOption): void {
    const current = this.proposal();
    if (!current) return;
    this.selectedTiming.set(option.userTime);
    this.proposal.set({
      ...current,
      timing: option.label,
      userTime: option.userTime,
      dueDate: option.dueDate,
      lockedDay: option.lockedDay,
      lockedAt: option.lockedAt,
      recurrenceRule: option.recurrence,
      recurrence: option.recurrence ? this.recurrenceLabel(option.recurrence) : current.recurrence,
      selectedTimeOptionId: option.id,
      manualDate: option.dueDate ?? option.lockedDay ?? option.lockedAt?.slice(0, 10),
      manualTime: option.lockedAt?.slice(11, 16),
    });
  }

  updateProposalDate(day: string): void {
    const current = this.proposal();
    if (!current) return;
    const time = this.proposalTime(current);
    this.proposal.set({ ...current, userTime: "manual", timing: day || "Zelf gekozen", dueDate: day || undefined, lockedDay: day || undefined, lockedAt: day && time ? `${day}T${time}:00.000` : undefined, manualDate: day || undefined, manualTime: time || undefined, timeOptions: [], selectedTimeOptionId: undefined });
  }

  updateProposalTime(time: string): void {
    const current = this.proposal();
    if (!current) return;
    const day = this.proposalDate(current) || this.todayISO();
    this.proposal.set({ ...current, userTime: "manual", timing: `${day} ${time || ""}`.trim(), dueDate: day, lockedDay: day, lockedAt: time ? `${day}T${time}:00.000` : undefined, manualDate: day, manualTime: time || undefined, timeOptions: [], selectedTimeOptionId: undefined });
  }

  updateProposalBrainCell(brainCellId: string): void {
    const current = this.proposal();
    if (!current) return;
    const cell = this.brainCells().find((item) => item.id === brainCellId);
    this.proposal.set({ ...current, brainCellId, room: cell?.label ?? current.room, tone: this.toneForRoom(cell?.label ?? current.room), astraId: undefined, astra: "Geen directe richting nodig" });
  }

  updateProposalAstra(astraId: string | null): void {
    const current = this.proposal();
    if (!current) return;
    const astra = astraId ? this.astraRecords().find((item) => item.id === astraId) : undefined;
    const cell = astra ? this.brainCells().find((item) => item.id === astra.brainCellId) : undefined;
    this.proposal.set({ ...current, astraId: astra?.id, astra: astra?.text ?? "Geen directe richting nodig", brainCellId: astra?.brainCellId ?? current.brainCellId, room: cell?.label ?? current.room, tone: this.toneForRoom(cell?.label ?? current.room) });
  }

  proposalDate(proposal: Proposal): string {
    return proposal.manualDate ?? proposal.lockedAt?.slice(0, 10) ?? proposal.lockedDay ?? proposal.dueDate ?? "";
  }

  proposalTime(proposal: Proposal): string {
    return proposal.manualTime ?? proposal.lockedAt?.slice(11, 16) ?? "";
  }

  async quickSaveDraft(): Promise<void> {
    const title = (this.draftText() || this.inputText()).trim();
    const pseudonymId = this.pseudonymId();
    if (!title || !pseudonymId || this.savingDraft()) return;
    const timing = this.selectedTiming();
    const priority = this.selectedPriority() ?? "medium";
    const recurrence = this.selectedRecurrence();
    this.savingDraft.set(true);
    try {
      await this.withApiError(async () => {
        await this.api.createAction(pseudonymId, title, "small", buildQuickAddPayload({
          timing,
          priority,
          recurrence,
          timingPayload: timing ? this.timingPayload(timing) : undefined,
        }));
        this.resetInputDraft();
        this.screen.set("ahastra");
        this.helpMode.set(false);
        await this.refreshBootstrap();
        await this.refreshPlan();
        this.showRelief("Bewaard. Ahastra zet het op de juiste plek.", 2600);
      });
    } finally {
      this.savingDraft.set(false);
    }
  }

  async prepareProposal(raw: string, timing: TimingChoice | null = this.selectedTiming(), priority: PriorityChoice = this.selectedPriority() ?? "medium", recurrence: RecurrenceChoice = this.selectedRecurrence()): Promise<void> {
    const title = raw.trim();
    if (!title) return;
    const pseudonymId = this.pseudonymId();
    if (!pseudonymId) return;
    const timingPayload = timing ? this.timingPayload(timing) : {};
    await this.withApiError(async () => {
      const { proposal } = await this.api.proposeAction(pseudonymId, title, timing ?? undefined, priority, recurrence, timingPayload.dueDate, timingPayload.manualTime);
      const baseProposal = this.toProposal(proposal);
      const timedProposal = timing ? this.withTimingContext(baseProposal, timing, priority, recurrence) : { ...baseProposal, userPriority: priority, recurrenceChoice: recurrence };
      this.proposal.set(timedProposal);
      this.draftText.set(timedProposal.title);
      this.inputFlow.set("proposal");
    });
  }

  async confirmProposal(): Promise<void> {
    const proposal = this.proposal();
    const pseudonymId = this.pseudonymId();
    if (!proposal || !pseudonymId) return;
    await this.withApiError(async () => {
      await this.api.createAction(pseudonymId, proposal.title, proposal.size, {
        brainCellId: proposal.brainCellId,
        astraId: proposal.astraId,
        dueDate: proposal.dueDate,
        lockedDay: proposal.lockedDay,
        lockedAt: proposal.lockedAt,
        nonNegotiable: proposal.nonNegotiable,
        userTime: proposal.userTime,
        userPriority: proposal.userPriority,
        recurrence: proposal.recurrenceRule ?? proposal.recurrenceChoice,
        manualTime: (proposal.manualTime ?? this.manualTime()) || undefined,
        selectedTimeOptionId: proposal.selectedTimeOptionId,
      });
      this.resetInputDraft();
      this.screen.set("ahastra");
      this.helpMode.set(false);
      await this.refreshBootstrap();
      await this.refreshPlan();
      this.showRelief("Bewaard. Ahastra kijkt opnieuw naar vandaag.", 2600);
    });
  }

  closeInputFlow(): void {
    this.resetInputDraft(false);
  }

  openAstra(astraId: string): void {
    this.selectedAstraId.set(astraId);
    this.selectedRoomId.set(null);
  }

  openRoom(roomId: string): void {
    this.selectedRoomId.set(roomId);
    this.selectedAstraId.set(null);
  }

  closeDirectionDetail(): void {
    this.selectedAstraId.set(null);
    this.selectedRoomId.set(null);
  }

  activeAstra(): AstraDirection | null {
    const selected = this.selectedAstraId();
    return this.astras().find((astra) => astra.id === selected) ?? null;
  }

  activeRoom(): BrainCellRoom | null {
    const selected = this.selectedRoomId();
    return this.rooms().find((room) => room.id === selected) ?? null;
  }

  startAddAstra(): void {
    this.openNewAstra();
  }

  openNewAstra(): void {
    this.newAstraText.set("");
    this.newAstraCellId.set(this.brainCells()[0]?.id ?? null);
    this.newAstraDay.set("");
    this.newAstraMonth.set("");
    this.newAstraYear.set("");
    this.newAstraOpen.set(true);
  }

  async createNewAstra(): Promise<void> {
    const pseudonymId = this.pseudonymId();
    const text = this.newAstraText().trim();
    const targetDate = this.newAstraTargetDate();
    if (!pseudonymId || !text) return this.showRelief("Geef je Astra een titel.", 2200);
    if (!targetDate) return this.showRelief("Kies een geldige doeldatum.", 2200);
    await this.withApiError(async () => {
      await this.api.createAstra(pseudonymId, text, targetDate, this.newAstraCellId() ?? undefined);
      await this.refreshBootstrap();
      this.newAstraOpen.set(false);
      this.showRelief("Astra toegevoegd.", 2200);
    });
  }

  private newAstraTargetDate(): string | undefined {
    const day = Number(this.newAstraDay().trim());
    const month = Number(this.newAstraMonth().trim());
    const year = Number(this.newAstraYear().trim());
    if (!Number.isInteger(day) || !Number.isInteger(month) || !Number.isInteger(year)) return undefined;
    if (year < 1900 || year > 2200 || month < 1 || month > 12 || day < 1 || day > 31) return undefined;
    const target = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    const parsed = new Date(`${target}T00:00:00.000Z`);
    return parsed.toISOString().slice(0, 10) === target ? target : undefined;
  }

  openProfile(sectionId: string): void {
    this.selectedProfileId.set(sectionId);
  }

  activeProfileSection(): ProfileSection | null {
    const selected = this.selectedProfileId();
    if (selected === "developer" && this.developerMode()) {
      return {
        id: "developer",
        title: "Developer Tools",
        status: `Dev clock ${this.activeAhastraLabel()}`,
        lines: ["Alleen voor beta-validatie.", "Deze waarde is exact de actieve tijd die de Priority Grid-aanvragen gebruiken."],
      };
    }
    return this.profileSections().find((section) => section.id === selected) ?? null;
  }

  developerTap(): void {
    const next = this.devTapCount() + 1;
    this.devTapCount.set(next);
    if (next >= 5) {
      this.developerUnlocked.set(true);
      globalThis.localStorage?.setItem(this.developerUnlockedKey(), "1");
      this.showRelief("Developer zichtbaar onder Jij → Profiel.", 2200);
    }
  }

  openDeveloperTools(): void {
    if (this.developerMode()) this.selectedProfileId.set("developer");
  }

  async toggleDeveloperMode(): Promise<void> {
    const enabled = !this.developerMode();
    this.developerMode.set(enabled);
    globalThis.localStorage?.setItem(this.developerModeKey(), enabled ? "1" : "0");
    this.refreshLocalClock();
    await this.logUsage("developer_clock_changed", { mode: enabled ? "enabled" : "disabled", offset: this.devDayOffset() });
    await this.refreshPlan();
  }

  async advanceDevClock(days: 1 | 3 | 7): Promise<void> {
    this.developerMode.set(true);
    this.devDayOffset.update((offset) => offset + days);
    this.persistDevClock();
    this.refreshLocalClock();
    await this.logUsage("developer_clock_changed", { days, offset: this.devDayOffset(), activeNow: this.activeNowISO() });
    await this.refreshBootstrap();
    await this.refreshPlan();
    this.showRelief(`Dev datum ${this.activeAhastraLabel()}.`, 2200);
  }

  async advanceDevTime(minutes: 60 | 240): Promise<void> {
    this.developerMode.set(true);
    this.devMinuteOffset.update((offset) => offset + minutes);
    this.persistDevClock();
    this.refreshLocalClock();
    await this.logUsage("developer_clock_changed", { minutes, offset: this.devMinuteOffset(), activeNow: this.activeNowISO() });
    await this.refreshBootstrap();
    await this.refreshPlan();
    this.showRelief(`Dev tijd ${this.activeAhastraLabel()}.`, 2200);
  }

  async restoreActualDate(): Promise<void> {
    this.devDayOffset.set(0);
    this.devMinuteOffset.set(0);
    this.developerMode.set(false);
    this.persistDevClock();
    this.refreshLocalClock();
    await this.logUsage("developer_clock_changed", { mode: "restored", offset: 0, activeNow: this.activeNowISO() });
    await this.refreshBootstrap();
    await this.refreshPlan();
    this.showRelief("Echte datum hersteld.", 2200);
  }

  async rerunClassification(): Promise<void> {
    const pseudonymId = this.pseudonymId();
    if (!pseudonymId || !this.developerMode()) return;
    await this.withApiError(async () => {
      const result = await this.api.rerunClassification(pseudonymId);
      await this.logUsage("classification_rerun", { actionCount: result.actions.length });
      await this.refreshBootstrap();
      await this.refreshPlan();
      this.showRelief("Classificatie opnieuw uitgevoerd.", 2200);
    });
  }

  async recalculatePriorities(): Promise<void> {
    if (!this.developerMode()) return;
    await this.logUsage("developer_clock_changed", { mode: "recalculate", offset: this.devDayOffset(), minutes: this.devMinuteOffset(), activeNow: this.activeNowISO() });
    await this.refreshPlan();
    this.showRelief("Priority Grid opnieuw berekend.", 2200);
  }

  async resetTestWorld(): Promise<void> {
    const pseudonymId = this.pseudonymId();
    if (!pseudonymId || !this.developerMode()) return;
    this.devDayOffset.set(0);
    this.devMinuteOffset.set(0);
    this.developerMode.set(true);
    this.persistDevClock();
    this.refreshLocalClock();
    this.clearLocalRunState();
    this.dayPlan.set(null);
    this.todaySet.set(null);
    this.priorities.set([]);
    this.daySetItems.set([]);
    this.overviewItems.set([]);
    this.debugRows.set([]);
    await this.withApiError(async () => {
      const reset = await this.api.resetTestWorld(pseudonymId);
      this.actions.set(reset.actions);
      this.calendarConnection.set(reset.calendarConnection);
      this.applyDayPlan(reset.dayPlan, reset.todaySet);
      await this.refreshDaySet();
      await this.refreshDebug();
      this.selectedProfileId.set("developer");
      const message = reset.syncStatus === "error"
        ? "Test World gereset. Agenda sync heeft aandacht nodig."
        : reset.imported > 0
          ? `Test World gereset. ${reset.imported} agenda-items vers opgehaald.`
          : "Test World gereset. Agenda opnieuw gecontroleerd.";
      this.showRelief(message, 3200);
    });
  }

  async applySafeReturn(days: SafeReturnDays): Promise<void> {
    const pseudonymId = this.pseudonymId();
    if (!pseudonymId) return;
    await this.withApiError(async () => {
      this.returnDays.set(days);
      this.helpMode.set(false);
      this.selectedActionId.set(null);
      this.selectedProfileId.set(null);
      this.safeReturnPromptDays.set(null);
      this.closeDirectionDetail();
      this.screen.set("ahastra");
      this.showRelief(null);
      const { dayPlan } = await this.api.safeReturnDayPlan(pseudonymId, days, this.todayISO());
      this.applyDayPlan(dayPlan);
      await this.refreshDebug();
      globalThis.history.replaceState(null, "", "#ahastra");
    });
  }

  connectCalendar(): void {
    const pseudonymId = this.pseudonymId();
    if (!pseudonymId) return;
    globalThis.location.href = this.api.googleCalendarStartUrl(pseudonymId);
  }

  async importCalendar(): Promise<void> {
    const pseudonymId = this.pseudonymId();
    if (!pseudonymId) return;
    await this.withApiError(async () => {
      const imported = await this.api.importCalendarEvents(pseudonymId, this.todayISO());
      this.applyTodaySet(imported.todaySet);
      await this.refreshBootstrap();
      if (imported.dayPlan) {
        this.applyDayPlan(imported.dayPlan, imported.todaySet);
        await this.refreshDaySet();
        await this.refreshDebug();
      } else {
        await this.refreshPlan();
      }
      this.showRelief(imported.imported > 0 ? "Agenda opgehaald. Ahastra zet vandaag klaar." : "Agenda bijgewerkt. Geen nieuwe items voor vandaag gevonden.", 2600);
    });
  }

  async disconnectCalendar(): Promise<void> {
    const pseudonymId = this.pseudonymId();
    if (!pseudonymId) return;
    await this.withApiError(async () => {
      await this.api.disconnectCalendar(pseudonymId);
      await this.refreshBootstrap();
      await this.refreshPlan();
      this.showRelief("Google Calendar losgekoppeld.", 2600);
    });
  }

  toneClass(tone: Tone): string {
    return `tone-${tone}`;
  }

  private initialScreen(): CoreScreen {
    const hash = globalThis.location?.hash;
    if (hash === "#debug" && !environment.production && !environment.mvp1TestMode) return "debug";
    if (environment.mvp1TestMode) return "ahastra";
    if (hash === "#richting") return "richting";
    if (hash === "#jij" || hash === "#jij-dev") return "jij";
    return "ahastra";
  }

  private initialDeveloperUnlocked(): boolean {
    return globalThis.location?.hash === "#jij-dev" || globalThis.localStorage?.getItem(this.developerUnlockedKey()) === "1";
  }

  private initialDeveloperMode(): boolean {
    return globalThis.localStorage?.getItem(this.developerModeKey()) === "1";
  }

  private initialDevDayOffset(): number {
    const parsed = Number(globalThis.localStorage?.getItem(this.developerOffsetKey()) ?? "0");
    return Number.isInteger(parsed) && Math.abs(parsed) <= 90 ? parsed : 0;
  }

  private initialDevMinuteOffset(): number {
    const parsed = Number(globalThis.localStorage?.getItem(this.developerMinuteOffsetKey()) ?? "0");
    return Number.isInteger(parsed) && Math.abs(parsed) <= 90 * 24 * 60 ? parsed : 0;
  }

  private persistDevClock(): void {
    globalThis.localStorage?.setItem(this.developerModeKey(), this.developerMode() ? "1" : "0");
    globalThis.localStorage?.setItem(this.developerOffsetKey(), String(this.devDayOffset()));
    globalThis.localStorage?.setItem(this.developerMinuteOffsetKey(), String(this.devMinuteOffset()));
  }

  private async initializeIntelligence(): Promise<void> {
    await this.withApiError(async () => {
      const installation = await this.api.createInstallation(this.localInstallSeed());
      this.pseudonymId.set(installation.user.pseudonymId);
      this.restoreStartedActions(installation.user.pseudonymId);
      this.restorePromotedActions(installation.user.pseudonymId);
      this.evaluateSafeReturnPrompt(installation.user.pseudonymId);
      void this.logUsage("session_started", { view: this.screen() });
      void this.logUsage("page_loaded", { view: this.screen() });
      let bootstrap = await this.refreshBootstrap();
      if (!environment.production && this.localResetMode() === "demo" && !bootstrap.actions.length && !bootstrap.brainCells.length && !bootstrap.astras.length) {
        await this.api.resetDevData("demo", this.localInstallSeed(), this.todayISO());
        bootstrap = await this.refreshBootstrap();
      }
      if (!bootstrap.brainCells.length) {
        await this.api.saveOnboarding(installation.user.pseudonymId);
        bootstrap = await this.refreshBootstrap();
      }
      if (!bootstrap.latestMood) await this.api.saveMood(installation.user.pseudonymId, "prima", "small");
      await this.refreshBootstrap();
      await this.refreshPlan();
    });
  }

  private async refreshBootstrap() {
    const pseudonymId = this.pseudonymId();
    if (!pseudonymId) throw new Error("Ahastra installatie ontbreekt.");
    const bootstrap = await this.api.bootstrap(pseudonymId);
    this.brainCells.set(bootstrap.brainCells);
    this.astraRecords.set(bootstrap.astras);
    this.actions.set(bootstrap.actions);
    this.calendarConnection.set(bootstrap.calendarConnection);
    return bootstrap;
  }

  private async refreshPlan(mode?: "overload"): Promise<void> {
    const pseudonymId = this.pseudonymId();
    if (!pseudonymId) return;
    const day = this.todayISO();
    if (mode === "overload") {
      const { dayPlan } = await this.api.rebuildOverloadDayPlan(pseudonymId, day);
      this.applyDayPlan(dayPlan);
    } else {
      const { dayPlan, todaySet } = await this.api.rebuildDayPlan(pseudonymId, day);
      this.applyDayPlan(dayPlan, todaySet);
      await this.refreshDaySet();
    }
    await this.refreshDebug();
  }

  private async refreshDaySet(): Promise<void> {
    const pseudonymId = this.pseudonymId();
    if (!pseudonymId) return;
    try {
      const { items, todaySet } = await this.api.daySet(pseudonymId, this.todayISO());
      this.applyTodaySet(todaySet);
      this.daySetItems.set(items.map((item) => this.toPriorityItem(item)));
    } catch {
      this.daySetItems.set([]);
    }
  }

  private async refreshDebug(): Promise<void> {
    const pseudonymId = this.pseudonymId();
    if (!pseudonymId || environment.production) return;
    try {
      const { items } = await this.api.priorityDebug(pseudonymId, this.todayISO());
      this.debugRows.set(items);
    } catch {
      this.debugRows.set([]);
    }
  }

  private async withApiError(work: () => Promise<void>): Promise<void> {
    try {
      await work();
    } catch (error) {
      this.showRelief(error instanceof Error ? error.message : "Ahastra kon dit niet uitvoeren.");
    }
  }

  private showRelief(message: string | null, durationMs?: number): void {
    if (this.reliefFadeTimer) globalThis.clearTimeout(this.reliefFadeTimer);
    if (this.reliefClearTimer) globalThis.clearTimeout(this.reliefClearTimer);
    this.reliefFadeTimer = null;
    this.reliefClearTimer = null;
    this.reliefFading.set(false);
    this.reliefMessage.set(message);
    if (!message || !durationMs) return;
    const fadeMs = 320;
    this.reliefFadeTimer = globalThis.setTimeout(() => {
      if (this.reliefMessage() === message) this.reliefFading.set(true);
      this.reliefFadeTimer = null;
    }, Math.max(0, durationMs - fadeMs));
    this.reliefClearTimer = globalThis.setTimeout(() => {
      if (this.reliefMessage() === message) this.reliefMessage.set(null);
      this.reliefFading.set(false);
      this.reliefClearTimer = null;
    }, durationMs);
  }

  private showCompletionFeedback(title: string, message: string): void {
    if (this.completionTimer) globalThis.clearTimeout(this.completionTimer);
    this.completionFeedback.set({ title, message });
    this.completionTimer = globalThis.setTimeout(() => {
      this.completionFeedback.set(null);
      this.completionTimer = null;
    }, 2_200);
  }

  private async logUsage(type: UsageEventType, payload: Record<string, unknown> = {}): Promise<void> {
    const pseudonymId = this.pseudonymId();
    if (!pseudonymId) return;
    try {
      await this.api.recordUsageEvent(pseudonymId, type, payload);
    } catch {
      // Usage logging must never block the core ADHD-support flow.
    }
  }

  private resetInputDraft(clearText = true): void {
    this.inputFlow.set("idle");
    if (clearText) this.inputText.set("");
    this.draftText.set("");
    this.selectedTiming.set(null);
    this.selectedPriority.set(null);
    this.selectedRecurrence.set("none");
    this.manualDate.set("");
    this.manualTime.set("");
    this.proposal.set(null);
  }

  private timingPayload(timing: TimingChoice): { readonly dueDate?: string; readonly lockedDay?: string; readonly lockedAt?: string; readonly manualTime?: string } {
    const today = this.todayISO();
    const tomorrow = this.addDaysISO(today, 1);
    if (timing === "now") return { dueDate: today, lockedDay: today };
    if (timing === "today") return { dueDate: today };
    if (timing === "tonight") return { dueDate: today, lockedDay: today, lockedAt: `${today}T20:00:00.000`, manualTime: "20:00" };
    if (timing === "tomorrow") return { dueDate: tomorrow, lockedDay: tomorrow };
    if (timing === "this_week") return { dueDate: this.addDaysISO(today, 6) };
    if (timing === "next_week") return { dueDate: this.addDaysISO(today, 7) };
    if (timing === "manual") {
      const dueDate = this.manualDate() || undefined;
      const manualTime = this.manualTime() || undefined;
      return { dueDate, lockedDay: dueDate, lockedAt: dueDate && manualTime ? `${dueDate}T${manualTime}:00.000` : undefined, manualTime };
    }
    return {};
  }

  private timingLabel(timing: TimingChoice): string {
    if (timing === "now") return "Nu";
    if (timing === "today") return "Vandaag";
    if (timing === "tonight") return "Vanavond";
    if (timing === "tomorrow") return "Morgen";
    if (timing === "this_week") return "Deze week";
    if (timing === "next_week") return "Volgende week";
    if (timing === "manual") return this.manualDate() || "Zelf gekozen";
    return "Later";
  }

  private recurrenceLabel(rule: RecurrenceRule): string {
    if (rule === "daily") return "Elke dag";
    if (rule === "weekly") return "Elke week";
    if (rule === "monthly") return "Elke maand";
    if (rule.frequency === "weekdays") return rule.schoolBound ? "Elke schooldag" : "Elke werkdag";
    if (rule.frequency === "weekly") return "Elke week";
    if (rule.frequency === "monthly") return "Elke maand";
    return "Elke dag";
  }

  private applyDayPlan(dayPlan: DayPlan, todaySet?: TodaySet): void {
    const homeItems = dayPlan.items.map((item) => this.toPriorityItem(item));
    this.applyTodaySet(todaySet);
    this.dayPlan.set(dayPlan);
    this.priorities.set(homeItems);
  }

  private applyTodaySet(todaySet: TodaySet | undefined): void {
    if (!todaySet) return;
    this.todaySet.set(todaySet);
    this.currentDay.set(todaySet.ahastraDay);
  }

  private clearStartedAction(actionId: string): void {
    this.startedActionIds.update((ids) => {
      const next = new Set(ids);
      next.delete(actionId);
      this.persistStartedActions(next);
      return next;
    });
    this.startedCheckInVisible.set(false);
  }

  private persistStartedActions(ids: ReadonlySet<string>): void {
    const pseudonymId = this.pseudonymId();
    if (!pseudonymId) return;
    globalThis.localStorage?.setItem(this.startedKey(pseudonymId), JSON.stringify([...ids]));
  }

  private restoreStartedActions(pseudonymId: string): void {
    try {
      const raw = globalThis.localStorage?.getItem(this.startedKey(pseudonymId));
      const parsed = raw ? JSON.parse(raw) : [];
      const ids = Array.isArray(parsed) ? new Set(parsed.filter((item): item is string => typeof item === "string")) : new Set<string>();
      this.startedActionIds.set(ids);
      this.startedCheckInVisible.set(false);
    } catch {
      this.startedActionIds.set(new Set<string>());
      this.startedCheckInVisible.set(false);
    }
  }

  private startedKey(pseudonymId: string): string {
    return `ahastra.local.started.${pseudonymId}`;
  }

  private clearPromotedAction(actionId: string): void {
    this.promotedActionIds.update((ids) => {
      const next = new Set(ids);
      next.delete(actionId);
      this.persistPromotedActions(next);
      return next;
    });
  }

  private persistPromotedActions(ids: ReadonlySet<string>): void {
    const pseudonymId = this.pseudonymId();
    if (!pseudonymId) return;
    globalThis.localStorage?.setItem(this.promotedKey(pseudonymId), JSON.stringify([...ids]));
  }

  private restorePromotedActions(pseudonymId: string): void {
    try {
      const raw = globalThis.localStorage?.getItem(this.promotedKey(pseudonymId));
      const parsed = raw ? JSON.parse(raw) : [];
      this.promotedActionIds.set(Array.isArray(parsed) ? new Set(parsed.filter((item): item is string => typeof item === "string")) : new Set<string>());
    } catch {
      this.promotedActionIds.set(new Set<string>());
    }
  }

  private promotedKey(pseudonymId: string): string {
    return `ahastra.local.promoted.${pseudonymId}`;
  }

  private developerUnlockedKey(): string {
    return "ahastra.dev.unlocked";
  }

  private developerModeKey(): string {
    return "ahastra.dev.mode";
  }

  private developerOffsetKey(): string {
    return "ahastra.dev.dayOffset";
  }

  private developerMinuteOffsetKey(): string {
    return "ahastra.dev.minuteOffset";
  }

  private maybeOpenDayDone(): void {
    if (this.todaySet()?.isDayDone === true && (this.completedToday().length > 0 || this.skippedToday().length > 0)) {
      if (this.dayDoneFadeTimer) globalThis.clearTimeout(this.dayDoneFadeTimer);
      this.dayDoneOpen.set(true);
      this.dayDoneSummaryVisible.set(true);
      this.dayDoneFadeTimer = globalThis.setTimeout(() => {
        this.dayDoneSummaryVisible.set(false);
        this.dayDoneFadeTimer = null;
      }, 5_000);
    }
  }


  closeDayDone(): void {
    if (this.dayDoneFadeTimer) globalThis.clearTimeout(this.dayDoneFadeTimer);
    this.dayDoneFadeTimer = null;
    this.dayDoneOpen.set(false);
    this.dayDoneSummaryVisible.set(false);
  }

  private orderPriorityItems(items: readonly PriorityItem[]): readonly PriorityItem[] {
    const byId = new Map<string, PriorityItem>();
    for (const id of this.promotedActionIds()) {
      const action = this.actions().find((candidate) => candidate.id === id && candidate.status === "open" && this.actionVisibleToday(candidate));
      if (action) byId.set(id, this.toHiddenPriorityItem(action));
    }
    for (const item of items) byId.set(item.id, item);
    const pool = [...byId.values()].filter((item) => this.actionForItemIsOpen(item));
    const started = pool.filter((item) => this.actionStarted(item.id)).sort((left, right) => this.chronologicalCompare(left.id, right.id));
    const startedIds = new Set(started.map((item) => item.id));
    const promotedOrder = [...this.promotedActionIds()];
    const promoted = pool
      .filter((item) => !startedIds.has(item.id) && this.isPromoted(item.id))
      .sort((left, right) => promotedOrder.indexOf(left.id) - promotedOrder.indexOf(right.id));
    const pinnedIds = new Set([...startedIds, ...promoted.map((item) => item.id)]);
    const rest = pool.filter((item) => !pinnedIds.has(item.id));
    return [...started, ...promoted, ...rest];
  }

  private actionForItemIsOpen(item: PriorityItem): boolean {
    return this.actions().find((action) => action.id === item.id)?.status === "open";
  }

  private homeSelectableItem(item: PriorityItem): boolean {
    return this.actionForItemIsOpen(item) && this.isTodayItem(item);
  }

  private chronologicalCompare(leftId: string, rightId: string): number {
    const left = this.actions().find((action) => action.id === leftId);
    const right = this.actions().find((action) => action.id === rightId);
    return this.actionChronology(left).localeCompare(this.actionChronology(right));
  }

  private actionChronology(action: UserAction | undefined): string {
    return action?.lockedAt ?? action?.deferredUntil ?? action?.lockedDay ?? action?.dueDate ?? action?.id ?? "9999-12-31T23:59:59.999";
  }

  private actionVisibleToday(action: UserAction): boolean {
    return this.todaySetRankableIds().has(action.id);
  }


  private isTodayItem(item: PriorityItem): boolean {
    return this.todaySetRankableIds().has(item.id);
  }

  private toProposal(proposal: ActionProposal): Proposal {
    return {
      title: proposal.title,
      receipt: proposal.receipt ?? "Ik bewaar dit en zet het op de juiste plek.",
      room: proposal.brainCellLabel,
      tone: this.toneForRoom(proposal.brainCellLabel),
      astra: proposal.astraLabel,
      timing: proposal.timing,
      attention: proposal.attention,
      priority: proposal.priority,
      recurrence: proposal.recurrence,
      timeOptions: proposal.timeOptions ?? [],
      selectedTimeOptionId: proposal.selectedTimeOptionId,
      brainCellId: proposal.brainCellId,
      astraId: proposal.astraId,
      dueDate: proposal.dueDate,
      lockedDay: proposal.lockedDay,
      lockedAt: proposal.lockedAt,
      manualDate: proposal.lockedAt?.slice(0, 10) ?? proposal.lockedDay ?? proposal.dueDate,
      manualTime: proposal.lockedAt?.slice(11, 16),
      nonNegotiable: proposal.nonNegotiable,
      size: proposal.size,
    };
  }

  private buildHiddenActionGroups(items: readonly PriorityItem[]): readonly HiddenActionGroup[] {
    const groupOrder: readonly HiddenActionGroup["id"][] = ["today", "tomorrow", "this_week", "next_week", "later"];
    const titles: Record<HiddenActionGroup["id"], string> = { today: "Today", tomorrow: "Tomorrow", this_week: "This week", next_week: "Next week", later: "Later" };
    const grouped = new Map<HiddenActionGroup["id"], PriorityItem[]>(groupOrder.map((id) => [id, []]));
    for (const item of items) grouped.get(this.hiddenGroupForAction(this.actions().find((action) => action.id === item.id)))?.push(item);
    return groupOrder
      .map((id) => ({ id, title: titles[id], count: grouped.get(id)?.length ?? 0, items: grouped.get(id) ?? [] }))
      .filter((group) => group.count > 0);
  }

  private hiddenGroupForAction(action: UserAction | undefined): HiddenActionGroup["id"] {
    const today = this.todayISO();
    const day = (action?.lockedAt ?? action?.lockedDay ?? action?.dueDate ?? action?.deferredUntil)?.slice(0, 10);
    if (!day) return "later";
    if (day <= today) return "today";
    const diff = this.daysBetween(today, day);
    if (diff === 1) return "tomorrow";
    if (diff > 1 && diff < 7) return "this_week";
    if (diff >= 7 && diff < 14) return "next_week";
    return "later";
  }

  private toPriorityItem(item: DayPlanItem): PriorityItem {
    const action = this.actions().find((candidate) => candidate.id === item.actionId);
    const brainCellId = item.brainCellId ?? action?.brainCellId;
    const astraId = item.astraId ?? action?.astraId;
    const room = this.roomLabel(action, brainCellId);
    const source = item.source ?? action?.source ?? "manual";
    const timingChip = item.timingLabel ?? this.timingChipForAction(action, item.lockedAt);
    const isOverdueVisible = this.todaySet()?.overdueIds.includes(item.actionId) === true || item.placementState === "overdue";
    return {
      id: item.actionId,
      title: item.title,
      room,
      tone: this.toneForRoom(room),
      brainCellId,
      astraId,
      detail: isOverdueVisible ? "Eerder gepland; nog open." : this.detailForAction(action, item),
      whyNow: item.reason || action?.reason || "Past bij nu.",
      timingChip: isOverdueVisible ? `Overdue · ${timingChip}` : timingChip,
      placementState: isOverdueVisible ? "overdue" : item.placementState,
      placementReason: item.placementReason ?? this.placementReasonForAction(action, item.reason),
      steps: this.actionSteps(item.title),
      calendar: source === "calendar" || Boolean(item.lockedAt),
      directionNote: this.directionNote(action, astraId),
      source,
      classificationOrigin: item.classificationOrigin ?? action?.classificationOrigin,
      calendarOwner: item.calendarOwner ?? action?.calendarOwner,
      recurrence: item.recurrence ?? action?.recurrence,
      urgent: this.isUrgentAction(action),
      urgentConsequence: this.urgentConsequenceForAction(action),
    };
  }

  private toHiddenPriorityItem(action: UserAction): PriorityItem {
    const room = this.roomLabel(action);
    return {
      id: action.id,
      title: action.title,
      room,
      tone: this.toneForRoom(room),
      brainCellId: action.brainCellId,
      astraId: action.astraId,
      detail: this.detailForHiddenAction(action),
      whyNow: action.reason || "Bewaard voor later.",
      timingChip: this.timingChipForAction(action),
      placementReason: this.placementReasonForAction(action, action.reason),
      steps: this.actionSteps(action.title),
      calendar: action.source === "calendar" || Boolean(action.lockedAt),
      directionNote: this.directionNote(action),
      source: action.source ?? "manual",
      classificationOrigin: action.classificationOrigin,
      calendarOwner: action.calendarOwner,
      recurrence: action.recurrence,
      urgent: this.isUrgentAction(action),
      urgentConsequence: this.urgentConsequenceForAction(action),
    };
  }

  private withTimingContext(proposal: Proposal, timing: TimingChoice, priority: PriorityChoice, recurrence: RecurrenceChoice): Proposal {
    const payload = this.timingPayload(timing);
    const priorityLabel = priority === "high" ? "Hoog" : priority === "low" ? "Laag" : "Medium";
    const recurrenceLabel = recurrence === "daily" ? "Elke dag" : recurrence === "weekly" ? "Elke week" : recurrence === "monthly" ? "Elke maand" : undefined;
    const selectedTimeOption: ProposedTimeOption | undefined = payload.lockedAt ? { id: "chosen-time", label: this.timingLabel(timing), userTime: "manual", dueDate: payload.dueDate, lockedDay: payload.lockedDay, lockedAt: payload.lockedAt, reason: "Gekozen door jou." } : undefined;
    return {
      ...proposal,
      timing: this.timingLabel(timing),
      priority: priorityLabel,
      recurrence: recurrenceLabel,
      userTime: timing,
      userPriority: priority,
      recurrenceChoice: recurrence,
      dueDate: payload.dueDate,
      lockedDay: payload.lockedDay,
      lockedAt: payload.lockedAt,
      manualDate: payload.dueDate,
      manualTime: payload.manualTime,
      timeOptions: selectedTimeOption ? [selectedTimeOption] : [],
      selectedTimeOptionId: selectedTimeOption?.id,
      nonNegotiable: timing === "now" || ((timing === "today" || timing === "tonight" || timing === "tomorrow" || timing === "manual") && priority === "high"),
    };
  }

  private actionForId(actionId: string | null): PriorityItem | null {
    if (!actionId) return null;
    return this.priorities().find((action) => action.id === actionId) ?? this.hiddenActionItems().find((action) => action.id === actionId) ?? null;
  }

  directionTitle(text: string): string {
    const title = text.trim().split(/\s+en\s+|\s+met\s+|,/u)[0] || text.trim() || "Richting";
    return title.length > 28 ? `${title.slice(0, 25)}…` : title;
  }

  private directionRecent(actions: readonly UserAction[]): readonly string[] {
    const source = actions.filter((action) => action.status === "completed").length ? actions.filter((action) => action.status === "completed") : actions;
    const titles = source.slice(0, 2).map((action) => action.title);
    return titles.length ? titles : ["Nog geen gekoppelde acties"];
  }

  private directionActions(actions: readonly UserAction[]): readonly string[] {
    const titles = actions.filter((action) => action.status === "open").slice(0, 3).map((action) => action.title);
    return titles.length ? titles : ["Nog geen open acties"];
  }

  private roomSense(label: string): string {
    const lower = label.toLowerCase();
    if (lower.includes("admin")) return "vaste zaken";
    if (lower.includes("huis")) return "rust in huis";
    if (lower.includes("gezond")) return "ritme bewaren";
    return "mensen en aandacht";
  }

  private roomHeld(cellId: string): readonly HeldItem[] {
    const held = this.actions()
      .filter((action) => action.brainCellId === cellId)
      .slice(0, 3)
      .map((action) => ({ type: this.heldType(action), title: action.title }));
    return held.length ? held : [{ type: "ruimte", title: "Nog niets vastgehouden" }];
  }

  private heldType(action: UserAction): string {
    if (action.source === "calendar") return "agenda";
    if (action.status === "completed") return "klaar";
    if (action.deferredUntil) return "later";
    return "actie";
  }

  private roomLabel(action: UserAction | undefined, brainCellId?: string): string {
    const cell = this.brainCells().find((item) => item.id === (brainCellId ?? action?.brainCellId));
    return cell?.label ?? (action?.source === "calendar" ? "Agenda" : "Persoonlijk");
  }

  private toneForRoom(room: string): Tone {
    const lower = room.toLowerCase();
    if (lower.includes("admin") || lower.includes("agenda")) return "admin";
    if (lower.includes("huis")) return "house";
    if (lower.includes("gezond")) return "health";
    if (lower.includes("werk") || lower.includes("ondernem")) return "work";
    return "personal";
  }

  private detailForAction(action: UserAction | undefined, item: DayPlanItem): string {
    if (action?.source === "calendar") return "Dit staat in je agenda en weegt mee in vandaag.";
    if ((action?.skipCount ?? 0) > 0) return "Deze is eerder later gezet en komt nu terug.";
    return item.reason || action?.reason || "Ahastra houdt de rest uit beeld.";
  }

  private detailForHiddenAction(action: UserAction): string {
    if (action.source === "calendar") return "Agenda-item, niet bovenaan voor nu.";
    if (action.deferredUntil) return "Bewaard voor later.";
    return action.reason || "Bewaard voor later.";
  }

  private timingChipForAction(action: UserAction | undefined, planLockedAt?: string): string {
    if (action?.timingLabel) return action.timingLabel;
    const lockedAt = action?.lockedAt ?? planLockedAt;
    const dueDate = action?.dueDate ?? action?.lockedDay;
    const today = this.todayISO();
    const tomorrow = this.addDaysISO(today, 1);
    if (lockedAt) return `${this.dayLabel(lockedAt.slice(0, 10))} · ${lockedAt.slice(11, 16)}`;
    if (dueDate === today) return "Today";
    if (dueDate === tomorrow) return "Tomorrow";
    if (dueDate) return this.dayLabel(dueDate);
    if (action?.deferredUntil) return `Later · ${this.dayLabel(action.deferredUntil)}`;
    return "Geen vaste datum";
  }

  private placementReasonForAction(action: UserAction | undefined, planReason: string | undefined): string {
    const today = this.todayISO();
    const tomorrow = this.addDaysISO(today, 1);
    const day = (action?.lockedAt ?? action?.lockedDay ?? action?.dueDate)?.slice(0, 10);
    if (action?.source === "calendar") return action.lockedAt ? `Agenda · ${this.dayLabel(day ?? today)} om ${action.lockedAt.slice(11, 16)}` : "agenda-item";
    if (day === today) return "Vandaag, want gepland voor vandaag";
    if (day === tomorrow) return action?.lockedAt ? `Morgen om ${action.lockedAt.slice(11, 16)}` : "Morgen, want gepland voor morgen";
    if (day && day > today) return day === this.addDaysISO(today, 7) ? "Later, want gepland voor volgende week" : `Later, want gepland voor ${action?.timingLabel ?? this.dayLabel(day)}`;
    if (action?.deferredUntil) return `Later, want uitgesteld tot ${this.dayLabel(action.deferredUntil)}`;
    if (action?.nonNegotiable) return "hoge aandacht of deadline";
    return planReason || action?.reason || "past bij de huidige planning";
  }

  private dayLabel(day: string): string {
    const today = this.todayISO();
    if (day === today) return "vandaag";
    if (day === this.addDaysISO(today, 1)) return "morgen";
    return day;
  }

  private isUrgentAction(action: UserAction | undefined): boolean {
    return Boolean(action?.nonNegotiable);
  }

  private urgentConsequenceForAction(action: UserAction | undefined): string {
    const title = action?.title.toLowerCase() ?? "";
    if (title.includes("huur") || title.includes("rent")) return "Als de huur later betaald wordt, kan dat extra kosten of gedoe met de verhuurder geven.";
    if (title.includes("belasting") || title.includes("tax")) return "Als dit later gebeurt, kan dat extra kosten of administratief gedoe geven.";
    return "Als dit later gebeurt, kan er een concreet gevolg ontstaan. Jij kiest wat nu klopt.";
  }

  private directionNote(action: UserAction | undefined, astraId = action?.astraId): string | undefined {
    if (!astraId) return undefined;
    return this.astraRecords().find((astra) => astra.id === astraId) ? "richting" : undefined;
  }

  private actionSteps(title: string): readonly string[] {
    return breakIntoSteps(title).steps;
  }

  private currentMicroStep(action: PriorityItem): string | undefined {
    return action.steps.find((step) => !this.stepDone(action.id, step));
  }

  private randomRecoveryTips(): readonly string[] {
    const tips = [...RECOVERY_TIP_POOL];
    for (let index = tips.length - 1; index > 0; index -= 1) {
      const swapIndex = Math.floor(Math.random() * (index + 1));
      [tips[index], tips[swapIndex]] = [tips[swapIndex], tips[index]];
    }
    return tips.slice(0, 3);
  }

  private evaluateSafeReturnPrompt(pseudonymId: string): void {
    const key = this.lastSeenKey(pseudonymId);
    const previous = globalThis.localStorage?.getItem(key);
    const daysAway = this.daysSince(previous);
    this.safeReturnPromptDays.set(daysAway >= 14 ? 14 : daysAway >= 7 ? 7 : daysAway >= 3 ? 3 : null);
    globalThis.localStorage?.setItem(key, new Date().toISOString());
  }

  private daysSince(value: string | null | undefined): number {
    if (!value) return 0;
    const previous = Date.parse(value);
    if (!Number.isFinite(previous)) return 0;
    return Math.floor((Date.now() - previous) / 86_400_000);
  }

  private lastSeenKey(pseudonymId: string): string {
    return `ahastra.local.lastSeenAt.${pseudonymId}`;
  }

  private todayISO(): string {
    return this.todaySet()?.ahastraDay ?? this.currentDay();
  }

  private activeNowISO(): string {
    return this.activeNow();
  }

  private systemTodayISO(): string {
    return this.isoDate(new Date());
  }

  private refreshLocalClock(): void {
    const active = this.computeActiveNowISO(new Date());
    const activeDay = this.activeDayFor(active);
    this.activeNow.set(active);
    this.currentDay.set(activeDay);
    if (this.todaySet()?.ahastraDay !== activeDay) this.todaySet.set(null);
  }

  private computeActiveNowISO(base: Date): string {
    const active = new Date(base);
    if (this.developerMode()) {
      active.setDate(active.getDate() + this.devDayOffset());
      active.setMinutes(active.getMinutes() + this.devMinuteOffset());
    }
    return `${this.isoDate(active)}T${String(active.getHours()).padStart(2, "0")}:${String(active.getMinutes()).padStart(2, "0")}:00.000`;
  }

  private async handleClockTick(): Promise<void> {
    const previousDay = this.currentDay();
    this.refreshLocalClock();
    if (this.currentDay() === previousDay || !this.pseudonymId()) return;
    this.completedToday.set([]);
    this.skippedToday.set([]);
    this.closeDayDone();
    await this.refreshBootstrap();
    await this.refreshPlan();
  }

  private activeDayFor(value: string): string {
    const day = value.slice(0, 10);
    const hour = Number(value.slice(11, 13));
    if (!/^\d{4}-\d{2}-\d{2}$/.test(day) || !Number.isInteger(hour) || hour >= 3) return day;
    return this.addDaysISO(day, -1);
  }

  private addDaysISO(day: string, days: number): string {
    const [year, month, date] = day.split("-").map(Number);
    const next = new Date(year, month - 1, date);
    next.setDate(next.getDate() + days);
    return this.isoDate(next);
  }

  private daysBetween(startDay: string, endDay: string): number {
    const start = Date.parse(`${startDay}T00:00:00.000Z`);
    const end = Date.parse(`${endDay}T00:00:00.000Z`);
    return Math.floor((end - start) / 86_400_000);
  }

  private isoDate(value: Date): string {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const date = String(value.getDate()).padStart(2, "0");
    return `${year}-${month}-${date}`;
  }

  private clearLocalRunState(): void {
    const pseudonymId = this.pseudonymId();
    if (pseudonymId) {
      globalThis.localStorage?.removeItem(this.startedKey(pseudonymId));
      globalThis.localStorage?.removeItem(this.promotedKey(pseudonymId));
    }
    this.todaySet.set(null);
    this.startedActionIds.set(new Set<string>());
    this.promotedActionIds.set(new Set<string>());
    this.completedStepIds.set(new Set<string>());
    this.microStepActionIds.set(new Set<string>());
    this.completedToday.set([]);
    this.skippedToday.set([]);
    this.dayDoneOpen.set(false);
    this.dayDoneSummaryVisible.set(false);
    this.selectedActionId.set(null);
    this.skipActionId.set(null);
    this.helpMode.set(false);
    this.inputFlow.set("idle");
    this.proposal.set(null);
  }

  private localResetMode(): DevResetMode {
    return globalThis.localStorage?.getItem(this.devResetModeKey()) === "empty" ? "empty" : "demo";
  }

  private devResetModeKey(): string {
    return "ahastra.local.resetMode";
  }

  private localInstallSeed(): string {
    const key = "ahastra.local.installSeed";
    const existing = globalThis.localStorage?.getItem(key);
    if (existing) return existing;
    const generated = globalThis.crypto?.randomUUID?.() ?? `local-${Date.now()}`;
    globalThis.localStorage?.setItem(key, generated);
    return generated;
  }
}
